import {
  ArrowTopRightOnSquareIcon,
  Cog8ToothIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  LinkIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { CopyIcon } from 'lucide-react';
import { Check, X } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { ThemeOption, useTheme } from '@/app/ThemeContext';
import { BaseTag, DashedTag, TabOption } from '@/components/chariot-ui';
import { NoInformation as NoInformationIcon } from '@/components/chariot-ui';
import { CopyToClipboard } from '@/components/CopyToClipboard';
import { EvidenceImage } from '@/components/legacy-ui/EvidenceImage';
import { Link } from '@/components/Link';
import { Loader } from '@/components/Loader';
import { MarkdownPreview } from '@/components/markdown/MarkdownPreview';
import { ProofRenderer } from '@/components/proof/ProofRenderer';
import { isStructuredProof } from '@/components/proof/types';
import { Table } from '@/components/table/Table';
import Tabs from '@/components/Tabs';
import { Button } from '@/components/ui/button';
import { useGetFile } from '@/hooks/useFiles';
import { LoginPanelRequestRow as LoginPanelRequestCell } from '@/sections/poe/components/LoginPanelRequestRow';
import { PoeContent as Content } from '@/sections/poe/components/PoeContent';
import { getRepoInfo } from '@/sections/poe/components/RepoInfo';
import {
  getVulnerabilityClassification,
  VulnerabilityClassification,
} from '@/sections/poe/components/VulnerabilityClassification';
import { MAIN_TAB_DEFS } from '@/sections/poe/constants/mainTabDefs';
import { processAugustusDataSource } from '@/sections/poe/processors/augustus.processor';
import { processConstantineDataSource } from '@/sections/poe/processors/constantine.processor';
import { processCrawlerDataSource } from '@/sections/poe/processors/crawler.processor';
import { processNucleiDataSource } from '@/sections/poe/processors/Nuclei.processor';
import { processOrcaDataSource } from '@/sections/poe/processors/Orca.processor';
import { processPortscanDataSource } from '@/sections/poe/processors/portscan.processor';
import { processSecretsDataSource } from '@/sections/poe/processors/Secrets.processor';
import { processSnykCodeDataSource } from '@/sections/poe/processors/Snyk.processor';
import { processSshDataSource } from '@/sections/poe/processors/ssh.processor';
import { processWhoisDataSource } from '@/sections/poe/processors/whois.processor';
import type {
  BurpDescriptiveEvidence,
  BurpPOE,
  ExtendedTabOption,
  GitHubPOE,
  MainTabKey,
  POEContent,
  POEProps,
  SectionContent,
  SummaryPOE,
} from '@/sections/poe/types/poe.types';
import {
  ProcessSegments,
  SegmentsToPlainText,
} from '@/sections/poe/utils/BurpSegments';
import { formatKey } from '@/sections/poe/utils/formatKey';
import { mapToMainTab } from '@/sections/poe/utils/mapToMainTab';
import { TabDescription } from '@/sections/TabDescription';
import { useGlobalState } from '@/state/global.state';
import { KeyboardListersId, Webpage } from '@/types';
import { cn } from '@/utils/classname';
import { formatDate } from '@/utils/date.util';
import { decode } from '@/utils/encrypt.util';

// Helper functions for Wiz evidence processing
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const extractWizProperty = (obj: any, prop: string): string | null => {
  if (!obj || typeof obj !== 'object') return null;

  // Handle both direct property access and string parsing
  if (typeof obj === 'string') {
    const match = obj.match(new RegExp(`${prop}:\\s*([^\\n•]+)`, 'i'));
    return match ? match[1].trim() : null;
  }

  return obj[prop] || null;
};

// Parse Wiz finding strings into structured data
const parseWizFinding = (findingStr: string) => {
  const lines = findingStr.split('\n');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const result: any = { properties: {} };

  let inProperties = false;
  for (const line of lines) {
    if (line.startsWith('Type: ')) {
      result.type = line.replace('Type: ', '').trim();
    } else if (line.startsWith('Name: ')) {
      result.name = line.replace('Name: ', '').trim();
    } else if (line.startsWith('ProviderURL: ')) {
      result.providerURL = line.replace('ProviderURL: ', '').trim();
    } else if (line.startsWith('Properties:')) {
      inProperties = true;
    } else if (inProperties && line.startsWith('• ')) {
      const propLine = line.replace('• ', '');
      const [key, ...valueParts] = propLine.split(': ');
      if (key && valueParts.length > 0) {
        result.properties[key] = valueParts.join(': ').trim();
      }
    }
  }

  return result;
};

const formatWizTimestamp = (timestamp: string | null): string => {
  if (!timestamp) return 'Unknown';
  try {
    return formatDate(timestamp);
  } catch {
    return timestamp;
  }
};

const getWizSeverityClass = (severity: string | null): string => {
  if (!severity) return '';
  const lower = severity.toLowerCase();
  if (lower.includes('critical')) return 'text-error font-medium';
  if (lower.includes('high')) return 'text-error font-medium';
  if (lower.includes('medium')) return 'text-warning font-medium';
  if (lower.includes('low')) return 'text-success font-medium';
  return 'text-chariot-text-tertiary';
};

const getHttpStatusClass = (status: string | null): string => {
  if (!status) return '';
  const code = parseInt(status);
  if (code >= 400) return 'text-error';
  if (code >= 300) return 'text-warning';
  if (code >= 200) return 'text-success';
  return 'text-chariot-text-tertiary';
};

const getExposureLevelClass = (level: string | null): string => {
  if (!level) return '';
  const lower = level.toLowerCase();
  if (lower.includes('high')) return 'text-error';
  if (lower.includes('medium')) return 'text-warning';
  if (lower.includes('low')) return 'text-success';
  return 'text-chariot-text-tertiary';
};

function getMainTabsForSource(source: string): MainTabKey[] {
  if (source.startsWith('nuclei') || source === 'cve-2025-55182') {
    return [
      'INFORMATION',
      'REQUEST',
      'RESPONSE',
      'DETECTION',
      'ATTRIBUTES',
      'LOGIN_PANEL',
    ];
  }
  if (source.startsWith('burp')) {
    return [
      'INFORMATION',
      'REMEDIATION',
      'REQUEST',
      'RESPONSE',
      'ATTRIBUTES',
      'LOGIN_PANEL',
    ];
  }
  if (source.startsWith('crawler')) {
    return ['INFORMATION', 'DETECTION', 'ATTRIBUTES', 'LOGIN_PANEL'];
  }
  if (source.startsWith('login-detection') || source.startsWith('loginpanel')) {
    return ['INFORMATION', 'LOGIN_PANEL', 'ATTRIBUTES'];
  }
  if (source.startsWith('snyk')) {
    return ['INFORMATION', 'DETECTION', 'ATTRIBUTES'];
  }
  // For secrets (repo-based and cloud-based), use DETECTION for individual findings
  // Matches: secrets, nebula-aws-find-secrets, nebula-azure-find-secrets, nebula-gcp-*-secrets
  if (
    source.startsWith('secrets') ||
    source.includes('find-secrets') ||
    source.includes('-secrets')
  ) {
    return ['INFORMATION', 'DETECTION', 'ATTRIBUTES'];
  }
  // Augustus LLM vulnerability scanner - uses REQUEST/RESPONSE for prompt/response pairs
  if (source.startsWith('augustus')) {
    return ['INFORMATION', 'DETECTION', 'ATTRIBUTES'];
  }
  // Constantine LLM source code vulnerability scanner - uses DETECTION for findings
  if (source.startsWith('constantine')) {
    return ['DETECTION', 'ATTRIBUTES'];
  }
  // Orca Security platform alerts
  if (source.startsWith('orca')) {
    return ['INFORMATION', 'DETECTION', 'ATTRIBUTES'];
  }
  // Hadrian API security findings
  if (source.startsWith('hadrian')) {
    return ['INFORMATION', 'DETECTION', 'REQUEST', 'RESPONSE', 'ATTRIBUTES'];
  }
  return ['INFORMATION', 'ATTRIBUTES', 'LOGIN_PANEL'];
}

// Helper function to get section content safely
const getSectionContent = (
  sections: Record<string, SectionContent>,
  label: string | React.ReactNode,
  copyContents?: Record<string, string>
): string | undefined => {
  if (typeof label !== 'string') return undefined;

  // Check copyContents first for Burp sections with plain text versions
  if (copyContents && copyContents[label]) {
    return copyContents[label];
  }

  const content = sections[label];
  if (!content) return undefined;

  if (typeof content === 'string') {
    return content;
  }
  if (Array.isArray(content)) {
    return content.join('\n');
  }
  if (React.isValidElement(content)) {
    return undefined;
  }
  return JSON.stringify(content);
};

// Helper function to get displayable content
const getDisplayContent = (
  sections: Record<string, SectionContent>,
  label: string | React.ReactNode
): SectionContent | undefined => {
  if (typeof label !== 'string') return undefined;
  return sections[label];
};

const processBurpDataSource = (
  poeData: POEContent,
  sections: Record<string, SectionContent>,
  tempTabs: Record<MainTabKey, TabOption[]>,
  mainTabKeys: MainTabKey[],
  copyContents?: Record<string, string>
) => {
  const burpIssue = (poeData as BurpPOE).issue;
  const evidence = burpIssue?.evidence;
  if (!evidence) return;

  // Add Information section
  if (burpIssue && mainTabKeys.includes('INFORMATION')) {
    sections['burp_information'] = (
      <div className="border-chariot-stroke overflow-hidden rounded-lg border">
        <table className="w-full">
          <tbody>
            {burpIssue.confidence && (
              <tr className="border-chariot-stroke border-b last:border-b-0">
                <td className="bg-chariot-background-secondary text-chariot-text-secondary w-1/3 px-4 py-3 font-medium">
                  Confidence
                </td>
                <td className="text-chariot-text-primary px-4 py-3">
                  {burpIssue.confidence}
                </td>
              </tr>
            )}
            {burpIssue.path && (
              <tr className="border-chariot-stroke border-b last:border-b-0">
                <td className="bg-chariot-background-secondary text-chariot-text-secondary w-1/3 px-4 py-3 font-medium">
                  Path
                </td>
                <td className="text-chariot-text-primary px-4 py-3 font-mono text-sm break-all">
                  {burpIssue.path}
                </td>
              </tr>
            )}
            {burpIssue.description_html && (
              <tr className="border-chariot-stroke border-b last:border-b-0">
                <td className="bg-chariot-background-secondary text-chariot-text-secondary w-1/3 px-4 py-3 align-top font-medium">
                  Description
                </td>
                <td className="text-chariot-text-primary px-4 py-3">
                  <div
                    className="prose prose-sm text-chariot-text-primary max-w-none overflow-x-auto"
                    dangerouslySetInnerHTML={{
                      __html: burpIssue.description_html,
                    }}
                  />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );

    tempTabs['INFORMATION'].push({
      label: 'Information',
      value: 'burp_information',
    });

    // Add separate Remediation section
    if (burpIssue.remediation_html && mainTabKeys.includes('REMEDIATION')) {
      sections['burp_remediation'] = (
        <div className="prose prose-sm text-chariot-text-primary max-w-none">
          <div
            dangerouslySetInnerHTML={{ __html: burpIssue.remediation_html }}
          />
        </div>
      );

      tempTabs['REMEDIATION'].push({
        label: 'Remediation',
        value: 'burp_remediation',
      });
    }
  }

  evidence.forEach((item, index) => {
    const addSection = (
      label: string,
      content: React.ReactNode,
      suffix = '',
      plainText?: string
    ) => {
      if (!content) return;
      const value = `${label.toLowerCase().replace(/\s+/g, '_')}${suffix}`;
      const mainTab = mapToMainTab(label, mainTabKeys);
      tempTabs[mainTab].push({ label, value });
      sections[value] = (
        <pre className="text-chariot-text-primary m-0 font-mono text-sm whitespace-pre-wrap">
          {content}
        </pre>
      );

      // Store plain text version for copying
      if (copyContents && plainText) {
        copyContents[value] = plainText;
      }
    };

    if ('request_segments' in item || 'response_segments' in item) {
      const isRequest = 'request_segments' in item;
      const segments = isRequest
        ? item.request_segments
        : item.response_segments;
      const pairIndex = Math.floor(index / 2) + 1;
      const label = isRequest
        ? `Request ${pairIndex}`
        : `Response ${pairIndex}`;
      const value = isRequest ? `request_${index}` : `response_${index}`;
      const mainTab = mapToMainTab(label, mainTabKeys);

      tempTabs[mainTab].push({ label, value });
      sections[value] = (
        <pre className="text-chariot-text-primary m-0 font-mono text-sm whitespace-pre-wrap">
          {ProcessSegments(segments || [])}
        </pre>
      );

      // Store plain text version for copying
      if (copyContents) {
        copyContents[value] = SegmentsToPlainText(segments || []);
      }
    } else {
      const descriptive = item as BurpDescriptiveEvidence;
      if (descriptive.title && descriptive.description_html) {
        const value = descriptive.title.toLowerCase().replace(/\s+/g, '_');
        const mainTab = mapToMainTab(descriptive.title, mainTabKeys);
        tempTabs[mainTab].push({ label: descriptive.title, value });
        sections[value] = (
          <div className="prose prose-sm text-chariot-text-primary max-w-none">
            {descriptive.description_html}
          </div>
        );

        if (descriptive.request?.length) {
          addSection(
            `${descriptive.title} - Request`,
            ProcessSegments(descriptive.request),
            '',
            SegmentsToPlainText(descriptive.request)
          );
        }
        if (descriptive.response?.length) {
          addSection(
            `${descriptive.title} - Response`,
            ProcessSegments(descriptive.response),
            '',
            SegmentsToPlainText(descriptive.response)
          );
        }
      }
    }
  });
};

// Helper function to format timestamps
const formatTimestamp = (timestamp: string | undefined): string => {
  if (!timestamp) return 'Unknown';

  try {
    // Handle Unix timestamp with timezone (e.g., "1407370977 -0700")
    const parts = timestamp.split(' ');
    const unixTime = parseInt(parts[0]) * 1000;
    const date = new Date(unixTime);

    if (parts[1]) {
      return `${date.toLocaleString()} (${parts[1]})`;
    }

    return date.toLocaleString();
  } catch {
    return timestamp;
  }
};

// Helper function to render git information with highlighting
// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
const renderGitInfo = (commit: any) => {
  const { commit_metadata, blob_path } = commit;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <h5 className="text-chariot-text-secondary mb-2 text-xs font-semibold">
            COMMIT DETAILS
          </h5>
          <div className="space-y-2 text-sm">
            <div className="flex items-center space-x-2">
              <span className="text-chariot-text-tertiary">ID:</span>
              <CopyToClipboard
                alwaysShowCopyIcon
                textToCopy={commit_metadata?.commit_id || ''}
              >
                <code className="bg-chariot-background-primary border-chariot-stroke text-chariot-text-primary rounded border px-2 py-1">
                  {commit_metadata?.commit_id?.substring(0, 8) || 'Unknown'}
                </code>
              </CopyToClipboard>
            </div>
            <div>
              <span className="text-chariot-text-tertiary">Message:</span>
              <div className="bg-chariot-background-primary border-chariot-stroke mt-1 rounded border p-2">
                <code className="text-chariot-text-primary text-sm">
                  {commit_metadata?.message || 'No message'}
                </code>
              </div>
            </div>
          </div>
        </div>

        <div>
          <h5 className="text-chariot-text-secondary mb-2 text-xs font-semibold">
            AUTHOR INFORMATION
          </h5>
          <div className="space-y-2 text-sm">
            <div>
              <span className="text-chariot-text-tertiary">Author:</span>
              <div className="mt-1 flex items-center space-x-2">
                <span className="bg-chariot-text-purple/10 text-chariot-text-purple inline-flex items-center rounded-md px-2 py-1 text-xs font-medium">
                  👤{' '}
                  {highlightAuthor(commit_metadata?.author_name || 'Unknown')}
                </span>
                <code className="text-chariot-text-secondary text-xs">
                  {highlightEmail(commit_metadata?.author_email || 'Unknown')}
                </code>
              </div>
            </div>
            <div>
              <span className="text-chariot-text-tertiary">Date:</span>
              <div className="text-chariot-text-primary mt-1">
                {formatTimestamp(commit_metadata?.author_timestamp)}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div>
        <h5 className="text-chariot-text-secondary mb-2 text-xs font-semibold">
          FILE INFORMATION
        </h5>
        <div className="flex items-center space-x-2">
          <span className="text-primary">📄</span>
          <CopyToClipboard alwaysShowCopyIcon textToCopy={blob_path || ''}>
            <code className="bg-chariot-background-primary border-chariot-stroke text-chariot-text-primary rounded border px-2 py-1">
              {highlightFilePath(blob_path || 'Unknown')}
            </code>
          </CopyToClipboard>
        </div>
      </div>
    </div>
  );
};

// Helper function to highlight author names with color coding
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const highlightAuthor = (authorName: string): React.ReactElement<any> => {
  return <span className="font-medium">{authorName}</span>;
};

// Helper function to highlight and format email addresses
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const highlightEmail = (email: string): React.ReactElement<any> => {
  const emailRegex = /^([^@]+)@(.+)$/;
  const match = email.match(emailRegex);

  if (match) {
    const [, username, domain] = match;
    return (
      <span>
        <span className="text-primary dark:text-primary-light font-medium">
          {username}
        </span>
        <span className="text-chariot-text-tertiary">@</span>
        <span className="text-success dark:text-success-light font-medium">
          {domain}
        </span>
      </span>
    );
  }

  return <span className="text-chariot-text-secondary">{email}</span>;
};

// Helper function to highlight file paths with directory structure
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const highlightFilePath = (filePath: string): React.ReactElement<any> => {
  const pathParts = filePath.split('/');
  const fileName = pathParts[pathParts.length - 1];
  const directory = pathParts.slice(0, -1).join('/');

  return (
    <span>
      {directory && (
        <>
          <span className="text-chariot-text-tertiary">{directory}/</span>
        </>
      )}
      <span className="text-chariot-text-primary font-semibold">
        {fileName}
      </span>
    </span>
  );
};

const processWizDataSource = (
  poeData: POEContent,
  sections: Record<string, SectionContent>,
  tempTabs: Record<MainTabKey, TabOption[]>,
  mainTabKeys: MainTabKey[]
): string[] => {
  // Return array of processed keys
  try {
    const contentStr =
      typeof poeData === 'string' ? poeData : JSON.stringify(poeData);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let wizData: any = null;
    let vulnerabilityDescription = '';
    let wizId = null;
    const processedKeys: string[] = []; // Track what keys we've processed

    // Handle different Wiz evidence formats:
    // 1. Pure JSON format (proofs files)
    // 2. Markdown format with vulnerability description (definitions files)
    // 3. Mixed format with embedded JSON

    try {
      wizData = JSON.parse(contentStr);
    } catch {
      // If not JSON, check for simple key-value format
      const lines = contentStr.split('\n');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const simpleData: any = {};
      let hasData = false;

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed || trimmed.startsWith('#')) continue;

        const colonIndex = trimmed.indexOf(':');
        if (colonIndex > 0) {
          const key = trimmed.substring(0, colonIndex).trim();
          const value = trimmed.substring(colonIndex + 1).trim();
          if (key && value) {
            simpleData[key] = value;
            hasData = true;
          }
        }
      }

      if (hasData) {
        wizData = simpleData;
      } else {
        // Try markdown format with possible embedded JSON
        const descMatch = contentStr.match(
          /#{1,4}\s+(?:Vulnerability\s+)?Description\s*([\s\S]*?)(?:--or--|$)/
        );
        if (descMatch) {
          vulnerabilityDescription = descMatch[1].trim();
        }

        // Look for embedded JSON resource data
        const resourceMatch = contentStr.match(/--or--\s*({.*})/s);
        if (resourceMatch) {
          try {
            wizData = JSON.parse(resourceMatch[1]);
          } catch (parseError) {
            console.warn('Failed to parse embedded resource data:', parseError);
          }
        }
      }
    }

    // Extract core Wiz information
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let resourceData: any = null;
    if (wizData) {
      wizId = wizData.wiz_id;
      if (!vulnerabilityDescription && wizData.description) {
        vulnerabilityDescription = wizData.description;
      }
      // Extract resource data from wizData
      resourceData = wizData;
    }

    // Handle simple key-value format (like wiz_id, os, cvss_base, version, fixed_version)
    let isSimpleFormat = false;
    let isSubscriptionFormat = false;
    let isMultiSectionFormat = false;

    if (wizData) {
      // Check if this looks like a multi-section format (bucket, data finding, endpoint, iam binding, service account)
      const multiSectionKeys = [
        'bucket',
        'data finding',
        'endpoint',
        'iam binding',
        'service account',
      ];
      const hasMultiSections = multiSectionKeys.some(key => wizData[key]);

      // Also check for direct dataFindingExamples array (alternative format)
      const hasDataFindingExamples =
        (Array.isArray(wizData.dataFindingExamples) &&
          wizData.dataFindingExamples.length > 0) ||
        (Array.isArray(wizData['Data Finding Examples']) &&
          wizData['Data Finding Examples'].length > 0);

      // Check for any large array that looks like phone number evidence
      const hasPhoneDataArray = Object.values(wizData).some(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (value: any) =>
          Array.isArray(value) &&
          value.length > 0 &&
          value[0] &&
          typeof value[0] === 'object' &&
          (value[0].value || value[0].valueForAnalytics || value[0].path)
      );

      // Check for PII data detection format (with DataCategory)
      const hasDataCategory =
        typeof wizData === 'object' &&
        (wizData.DataCategory === 'DataCategoryPII' ||
          wizData['Data Category'] === 'DataCategoryPII' ||
          hasDataFindingExamples ||
          hasPhoneDataArray);

      // SPECIFIC CHECK for the exact format you showed: "Data Finding Examples" key exists
      const hasDataFindingExamplesKey =
        wizData && wizData['Data Finding Examples'];

      if (
        hasMultiSections ||
        hasDataFindingExamples ||
        hasDataCategory ||
        hasDataFindingExamplesKey
      ) {
        isMultiSectionFormat = true;
      }
      // Check for older formats only if not multi-section
      else if (
        !wizData['configuration finding'] &&
        !wizData['endpoint'] &&
        !wizData['secret data']
      ) {
        // Check if this looks like a subscription format
        if (wizData.subscription && wizId) {
          isSubscriptionFormat = true;
        }
        // Check if this looks like a simple vulnerability format
        else {
          const hasVulnFields =
            wizData.cvss_base ||
            wizData.version ||
            wizData.fixed_version ||
            wizData.os;
          if (hasVulnFields && wizId) {
            isSimpleFormat = true;
          }
        }
      }
    }

    // Also check direct object properties for backwards compatibility
    if (typeof poeData === 'object' && poeData !== null) {
      if ('wiz_id' in poeData) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        wizId = (poeData as any).wiz_id;
      }

      Object.keys(poeData).forEach(key => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const value = (poeData as any)[key];
        if (
          key.toLowerCase().includes('description') &&
          typeof value === 'string'
        ) {
          if (!vulnerabilityDescription) {
            vulnerabilityDescription = value;
          }
        }
        if (key === 'subscription' && !resourceData) {
          resourceData = { subscription: value };
        }
      });
    }

    // Process Secret Data findings
    if (
      wizData &&
      wizData['secret data'] &&
      mainTabKeys.includes('DETECTION')
    ) {
      const secretData = wizData['secret data'];
      sections['wiz_secret_data'] = (
        <div className="space-y-4">
          <div className="border-error border-l-4 p-4">
            <div className="flex items-center">
              <ShieldExclamationIcon className="text-error mr-2 size-5" />
              <h4 className="text-chariot-text-primary font-semibold">
                Exposed Secret Data
              </h4>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="border-chariot-stroke rounded-lg border p-4">
              <h5 className="text-chariot-text-primary mb-3 font-medium">
                Certificate Details
              </h5>
              <div className="space-y-2 text-sm">
                {extractWizProperty(secretData, 'name') && (
                  <div>
                    <strong>Certificate:</strong>{' '}
                    {extractWizProperty(secretData, 'name')}
                  </div>
                )}
                {extractWizProperty(secretData, 'type') && (
                  <div>
                    <strong>Type:</strong>{' '}
                    <span className="text-chariot-text-secondary font-mono">
                      {extractWizProperty(secretData, 'type')?.replace(
                        'SecretType',
                        ''
                      )}
                    </span>
                  </div>
                )}
                {extractWizProperty(secretData, 'certificate_expiration') && (
                  <div>
                    <strong>Expires:</strong>{' '}
                    <span className="text-warning font-medium">
                      {formatWizTimestamp(
                        extractWizProperty(secretData, 'certificate_expiration')
                      )}
                    </span>
                  </div>
                )}
                {extractWizProperty(secretData, 'certificate_subjects') && (
                  <div>
                    <strong>Subjects:</strong>{' '}
                    <span className="font-mono text-xs">
                      {extractWizProperty(secretData, 'certificate_subjects')}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="border-chariot-stroke rounded-lg border p-4">
              <h5 className="text-chariot-text-primary mb-3 font-medium">
                Security Properties
              </h5>
              <div className="space-y-2 text-sm">
                {extractWizProperty(secretData, 'certificate_issuer') && (
                  <div>
                    <strong>Issuer:</strong>{' '}
                    {extractWizProperty(secretData, 'certificate_issuer')}
                  </div>
                )}
                {extractWizProperty(secretData, 'certificate_thumbprint') && (
                  <div>
                    <strong>Thumbprint:</strong>{' '}
                    <code className="bg-chariot-background-secondary rounded px-1 text-xs break-all">
                      {extractWizProperty(secretData, 'certificate_thumbprint')}
                    </code>
                  </div>
                )}
                {extractWizProperty(secretData, 'sha256') && (
                  <div>
                    <strong>SHA256:</strong>{' '}
                    <code className="bg-chariot-background-secondary rounded px-1 text-xs break-all">
                      {extractWizProperty(secretData, 'sha256')}
                    </code>
                  </div>
                )}
                {extractWizProperty(secretData, 'validationStatus') && (
                  <div>
                    <strong>Validation:</strong>{' '}
                    <span className="text-warning">
                      {extractWizProperty(
                        secretData,
                        'validationStatus'
                      )?.replace('SecretsValidationStatus', '')}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      );
      tempTabs['DETECTION'].push({
        label: 'Secret Data',
        value: 'wiz_secret_data',
      });
    }

    // Process Secret Instance findings
    if (
      wizData &&
      wizData['secret instance'] &&
      mainTabKeys.includes('DETECTION')
    ) {
      const secretInstance = wizData['secret instance'];
      sections['wiz_secret_instance'] = (
        <div className="space-y-4">
          <div className="border-warning border-l-4 p-4">
            <div className="flex items-center">
              <InformationCircleIcon className="text-warning mr-2 size-5" />
              <h4 className="text-chariot-text-primary font-semibold">
                Secret Instance Detection
              </h4>
            </div>
          </div>
          <div className="border-chariot-stroke rounded-lg border p-4">
            <h5 className="text-chariot-text-primary mb-3 font-medium">
              Detection Details
            </h5>
            <div className="space-y-3 text-sm">
              {extractWizProperty(secretInstance, 'path') && (
                <div className="flex items-start gap-3">
                  <span className="text-chariot-text-secondary min-w-16 font-medium">
                    Location:
                  </span>
                  <span className="bg-chariot-background-secondary rounded px-2 py-1 font-mono">
                    {extractWizProperty(secretInstance, 'path')}
                  </span>
                </div>
              )}
              {extractWizProperty(secretInstance, 'confidence') && (
                <div className="flex items-start gap-3">
                  <span className="text-chariot-text-secondary min-w-16 font-medium">
                    Confidence:
                  </span>
                  <span
                    className={`font-medium ${extractWizProperty(secretInstance, 'confidence') === 'ConfidenceHigh' ? 'text-error' : 'text-warning'}`}
                  >
                    {extractWizProperty(secretInstance, 'confidence')?.replace(
                      'Confidence',
                      ''
                    )}
                  </span>
                </div>
              )}
              {extractWizProperty(secretInstance, 'secretRuleId') && (
                <div className="flex items-start gap-3">
                  <span className="text-chariot-text-secondary min-w-16 font-medium">
                    Rule ID:
                  </span>
                  <code className="text-xs">
                    {extractWizProperty(secretInstance, 'secretRuleId')}
                  </code>
                </div>
              )}
              {extractWizProperty(secretInstance, 'severity') && (
                <div className="flex items-start gap-3">
                  <span className="text-chariot-text-secondary min-w-16 font-medium">
                    Severity:
                  </span>
                  <span
                    className={getWizSeverityClass(
                      extractWizProperty(secretInstance, 'severity')
                    )}
                  >
                    {extractWizProperty(secretInstance, 'severity')?.replace(
                      'Severity',
                      ''
                    )}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      );
      tempTabs['DETECTION'].push({
        label: 'Secret Instance',
        value: 'wiz_secret_instance',
      });
    }

    // DIRECT HANDLER: Process "Data Finding Examples" key specifically (the exact key you showed me)
    if (
      wizData &&
      wizData['Data Finding Examples'] &&
      Array.isArray(wizData['Data Finding Examples']) &&
      mainTabKeys.includes('DETECTION')
    ) {
      const phoneArray = wizData['Data Finding Examples'];
      sections['wiz_data_finding_examples'] = (
        <div className="space-y-4">
          <div className="border-error border-l-4 p-4">
            <div className="flex items-center">
              <ShieldExclamationIcon className="text-error mr-2 size-5" />
              <h4 className="text-chariot-text-primary font-semibold">
                PII Data Finding Examples
              </h4>
            </div>
          </div>

          <div className="border-chariot-stroke rounded-lg border p-4">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-chariot-text-secondary font-medium">
                Detected Phone Numbers:
              </span>
              <span className="text-chariot-text-tertiary text-xs">
                {phoneArray.length} total matches
              </span>
            </div>
            <div className="bg-chariot-background-secondary rounded-lg p-3">
              <div className="text-chariot-text-secondary mb-2 text-xs">
                Sample findings (showing first 10):
              </div>
              <div className="space-y-2">
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                {phoneArray.slice(0, 10).map((item: any, idx: number) => (
                  <div
                    key={idx}
                    className="border-chariot-stroke border-b pb-2 last:border-b-0"
                  >
                    <div className="mb-1 flex items-center justify-between">
                      <code className="text-chariot-text-primary font-mono text-sm">
                        {item.value || item.valueForAnalytics || 'Unknown'}
                      </code>
                      {item.pathTotalMatches && (
                        <span className="text-chariot-text-tertiary text-xs">
                          {item.pathTotalMatches.Value || item.pathTotalMatches}{' '}
                          matches
                        </span>
                      )}
                    </div>
                    {item.path && (
                      <div className="text-chariot-text-tertiary text-xs break-all">
                        {decodeURIComponent(item.path)}
                      </div>
                    )}
                  </div>
                ))}
                {phoneArray.length > 10 && (
                  <div className="border-chariot-stroke text-chariot-text-secondary border-t pt-2 text-xs italic">
                    ... and {phoneArray.length - 10} more findings
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      );
      tempTabs['DETECTION'].push({
        label: 'PII Findings',
        value: 'wiz_data_finding_examples',
      });
      processedKeys.push('Data Finding Examples');

      // CRITICAL: Remove the key from wizData to prevent any fallback JSON rendering
      delete wizData['Data Finding Examples'];
    }

    // Process Multi-Section Evidence (bucket, data finding, endpoint, iam binding, service account)
    else if (isMultiSectionFormat && mainTabKeys.includes('DETECTION')) {
      // Helper function to parse dataFindingExamples from data finding text or direct array
      const parseDataFindingExamples = (
        dataFindingText: string,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        wizData: any
      ) => {
        // First check if we have a direct dataFindingExamples array in wizData
        if (
          wizData.dataFindingExamples &&
          Array.isArray(wizData.dataFindingExamples)
        ) {
          return wizData.dataFindingExamples;
        }

        // Try to parse from text format
        const match = dataFindingText.match(/dataFindingExamples:\s*(\[.*\])/s);
        if (match) {
          try {
            const examples = JSON.parse(match[1]);
            return Array.isArray(examples) ? examples : [];
          } catch (e) {
            console.warn('Failed to parse data finding examples:', e);
          }
        }
        return [];
      };

      // Helper function to extract simple field from multi-line string
      const extractField = (
        text: string,
        fieldName: string
      ): string | undefined => {
        const lines = text.split('\n');
        for (const line of lines) {
          if (line.toLowerCase().includes(fieldName.toLowerCase())) {
            const parts = line.split(':');
            if (parts.length > 1) {
              return parts.slice(1).join(':').trim();
            }
          }
        }
        return undefined;
      };

      sections['wiz_multi_section'] = (
        <div className="space-y-6">
          <div className="border-error border-l-4 p-4">
            <div className="flex items-center">
              <ShieldExclamationIcon className="text-error mr-2 size-5" />
              <h4 className="text-chariot-text-primary font-semibold">
                Multi-Resource Security Finding
              </h4>
            </div>
          </div>

          {/* Wiz ID */}
          {wizId && (
            <div className="border-chariot-stroke rounded-lg border p-4">
              <div className="flex items-center justify-between">
                <h5 className="text-chariot-text-primary font-medium">
                  Wiz Finding ID
                </h5>
                <CopyToClipboard
                  iconClassName="size-3 text-chariot-text-secondary"
                  alwaysShowCopyIcon
                  textToCopy={wizId}
                >
                  <code className="bg-chariot-background-secondary rounded px-2 py-1 text-xs">
                    {wizId}
                  </code>
                </CopyToClipboard>
              </div>
            </div>
          )}

          {/* Bucket Section */}
          {wizData.bucket && (
            <div className="border-chariot-stroke rounded-lg border p-4">
              <h5 className="text-chariot-text-primary mb-3 flex items-center font-medium">
                <div className="bg-chariot-stroke-blue mr-2 size-4 rounded"></div>
                S3 Bucket Information
              </h5>
              <div className="space-y-2 text-sm">
                {(() => {
                  const bucketName = extractField(wizData.bucket, 'Name');
                  return (
                    bucketName && (
                      <div className="flex items-start gap-3">
                        <span className="text-chariot-text-secondary min-w-16 font-medium">
                          Bucket:
                        </span>
                        <CopyToClipboard
                          iconClassName="size-3 text-chariot-text-secondary"
                          alwaysShowCopyIcon
                          textToCopy={bucketName}
                        >
                          <code className="bg-chariot-background-secondary rounded px-2 py-1 font-mono">
                            {bucketName}
                          </code>
                        </CopyToClipboard>
                      </div>
                    )
                  );
                })()}
                {extractField(wizData.bucket, 'Type') && (
                  <div className="flex items-start gap-3">
                    <span className="text-chariot-text-secondary min-w-16 font-medium">
                      Type:
                    </span>
                    <span>{extractField(wizData.bucket, 'Type')}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Data Finding Section */}
          {wizData['data finding'] && (
            <div className="border-chariot-stroke rounded-lg border p-4">
              <h5 className="text-chariot-text-primary mb-3 flex items-center font-medium">
                <div className="bg-chariot-stroke-critical mr-2 size-4 rounded"></div>
                Data Finding - PII Detection
              </h5>
              <div className="space-y-3">
                <div className="space-y-2 text-sm">
                  {extractField(wizData['data finding'], 'Name') && (
                    <div className="flex items-start gap-3">
                      <span className="text-chariot-text-secondary min-w-20 font-medium">
                        Finding:
                      </span>
                      <span className="text-error font-medium">
                        {extractField(wizData['data finding'], 'Name')}
                      </span>
                    </div>
                  )}
                  {extractField(wizData['data finding'], 'Type') && (
                    <div className="flex items-start gap-3">
                      <span className="text-chariot-text-secondary min-w-20 font-medium">
                        Type:
                      </span>
                      <span>
                        {extractField(wizData['data finding'], 'Type')}
                      </span>
                    </div>
                  )}
                </div>

                {/* Data Finding Examples */}
                {(() => {
                  const examples = parseDataFindingExamples(
                    wizData['data finding'] || '',
                    wizData
                  );
                  if (examples.length > 0) {
                    return (
                      <div className="border-chariot-stroke border-t pt-3">
                        <div className="mb-2 flex items-center justify-between">
                          <span className="text-chariot-text-secondary font-medium">
                            Data Examples:
                          </span>
                          <span className="text-chariot-text-tertiary text-xs">
                            {examples.length} phone numbers found
                          </span>
                        </div>
                        <div className="bg-chariot-background-secondary rounded-lg p-3">
                          <div className="text-chariot-text-secondary mb-2 text-xs">
                            Sample detected phone numbers:
                          </div>
                          <div className="space-y-1">
                            {examples.slice(0, 5).map(
                              // eslint-disable-next-line @typescript-eslint/no-explicit-any
                              (example: any, idx: number) => (
                                <div
                                  key={idx}
                                  className="text-chariot-text-primary font-mono text-sm"
                                >
                                  {example.match || example}
                                </div>
                              )
                            )}
                            {examples.length > 5 && (
                              <div className="text-chariot-text-secondary text-xs italic">
                                ... and {examples.length - 5} more phone numbers
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            </div>
          )}

          {/* Endpoint Section */}
          {wizData.endpoint && (
            <div className="border-chariot-stroke rounded-lg border p-4">
              <h5 className="text-chariot-text-primary mb-3 flex items-center font-medium">
                <div className="bg-chariot-stroke-info mr-2 size-4 rounded"></div>
                API Endpoint
              </h5>
              <div className="space-y-2 text-sm">
                {(() => {
                  const endpointName = extractField(wizData.endpoint, 'Name');
                  return (
                    endpointName && (
                      <div className="flex items-start gap-3">
                        <span className="text-chariot-text-secondary min-w-16 font-medium">
                          URL:
                        </span>
                        <CopyToClipboard
                          iconClassName="size-3 text-chariot-text-secondary"
                          alwaysShowCopyIcon
                          textToCopy={endpointName}
                        >
                          <code className="bg-chariot-background-secondary rounded px-2 py-1 font-mono break-all">
                            {endpointName}
                          </code>
                        </CopyToClipboard>
                      </div>
                    )
                  );
                })()}
                {extractField(wizData.endpoint, 'Type') && (
                  <div className="flex items-start gap-3">
                    <span className="text-chariot-text-secondary min-w-16 font-medium">
                      Type:
                    </span>
                    <span>{extractField(wizData.endpoint, 'Type')}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* IAM Binding Section */}
          {wizData['iam binding'] && (
            <div className="border-chariot-stroke rounded-lg border p-4">
              <h5 className="text-chariot-text-primary mb-3 flex items-center font-medium">
                <div className="bg-chariot-stroke-medium mr-2 size-4 rounded"></div>
                IAM Permissions
              </h5>
              <div className="space-y-2 text-sm">
                {extractField(wizData['iam binding'], 'Name') && (
                  <div className="flex items-start gap-3">
                    <span className="text-chariot-text-secondary min-w-20 font-medium">
                      Permissions:
                    </span>
                    <span className="text-warning font-medium">
                      {extractField(wizData['iam binding'], 'Name')}
                    </span>
                  </div>
                )}
                {extractField(wizData['iam binding'], 'Type') && (
                  <div className="flex items-start gap-3">
                    <span className="text-chariot-text-secondary min-w-20 font-medium">
                      Type:
                    </span>
                    <span>{extractField(wizData['iam binding'], 'Type')}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Service Account Section */}
          {wizData['service account'] && (
            <div className="border-chariot-stroke rounded-lg border p-4">
              <h5 className="text-chariot-text-primary mb-3 flex items-center font-medium">
                <div className="bg-chariot-text-purple mr-2 size-4 rounded"></div>
                Service Account
              </h5>
              <div className="space-y-2 text-sm">
                {(() => {
                  const accountName = extractField(
                    wizData['service account'],
                    'Name'
                  );
                  return (
                    accountName && (
                      <div className="flex items-start gap-3">
                        <span className="text-chariot-text-secondary min-w-16 font-medium">
                          Account:
                        </span>
                        <CopyToClipboard
                          iconClassName="size-3 text-chariot-text-secondary"
                          alwaysShowCopyIcon
                          textToCopy={accountName}
                        >
                          <code className="bg-chariot-background-secondary rounded px-2 py-1 font-mono">
                            {accountName}
                          </code>
                        </CopyToClipboard>
                      </div>
                    )
                  );
                })()}
                {extractField(wizData['service account'], 'Type') && (
                  <div className="flex items-start gap-3">
                    <span className="text-chariot-text-secondary min-w-16 font-medium">
                      Type:
                    </span>
                    <span>
                      {extractField(wizData['service account'], 'Type')}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Handle direct dataFindingExamples array or any phone data array - this is the main case for your evidence */}
          {(() => {
            // Find the phone data array - could be dataFindingExamples, "Data Finding Examples", or any array with phone data structure
            let phoneDataArray = null;
            if (
              Array.isArray(wizData.dataFindingExamples) &&
              wizData.dataFindingExamples.length > 0
            ) {
              phoneDataArray = wizData.dataFindingExamples;
            } else if (
              Array.isArray(wizData['Data Finding Examples']) &&
              wizData['Data Finding Examples'].length > 0
            ) {
              phoneDataArray = wizData['Data Finding Examples'];
            } else {
              // Look for any array that looks like phone number evidence
              for (const value of Object.values(wizData)) {
                if (
                  Array.isArray(value) &&
                  value.length > 0 &&
                  value[0] &&
                  typeof value[0] === 'object' &&
                  (value[0].value ||
                    value[0].valueForAnalytics ||
                    value[0].path)
                ) {
                  phoneDataArray = value;
                  break;
                }
              }
            }

            return (
              phoneDataArray &&
              phoneDataArray.length > 0 && (
                <div className="border-chariot-stroke rounded-lg border p-4">
                  <h5 className="text-chariot-text-primary mb-3 flex items-center font-medium">
                    <div className="bg-chariot-stroke-critical mr-2 size-4 rounded"></div>
                    PII Data Detection - Phone Numbers
                  </h5>
                  <div className="space-y-3">
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-chariot-text-secondary font-medium">
                        Detected Examples:
                      </span>
                      <span className="text-chariot-text-tertiary text-xs">
                        {phoneDataArray.length} phone numbers found
                      </span>
                    </div>
                    <div className="bg-chariot-background-secondary rounded-lg p-3">
                      <div className="text-chariot-text-secondary mb-2 text-xs">
                        Sample detected phone numbers:
                      </div>
                      <div className="space-y-1">
                        {phoneDataArray.slice(0, 10).map(
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          (example: any, idx: number) => (
                            <div key={idx} className="space-y-1">
                              <div className="flex items-center justify-between text-sm">
                                <code className="text-chariot-text-primary font-mono">
                                  {example.value ||
                                    example.valueForAnalytics ||
                                    example.match ||
                                    example}
                                </code>
                                {example.pathTotalMatches && (
                                  <span className="text-chariot-text-tertiary text-xs">
                                    {example.pathTotalMatches.Value ||
                                      example.pathTotalMatches}{' '}
                                    matches
                                  </span>
                                )}
                              </div>
                              {example.path && (
                                <div className="text-chariot-text-tertiary text-xs break-all">
                                  Path: {decodeURIComponent(example.path)}
                                </div>
                              )}
                            </div>
                          )
                        )}
                        {phoneDataArray.length > 10 && (
                          <div className="border-chariot-stroke text-chariot-text-secondary border-t pt-2 text-xs italic">
                            ... and {phoneDataArray.length - 10} more phone
                            numbers
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )
            );
          })()}
        </div>
      );
      tempTabs['DETECTION'].push({
        label: 'Multi-Resource Finding',
        value: 'wiz_multi_section',
      });

      // Mark that we've handled the "Data Finding Examples" key to prevent fallback rendering
      if (wizData['Data Finding Examples']) {
        processedKeys.push('Data Finding Examples');
      }
    }

    // Process Configuration Findings (from proofs files)
    if (
      wizData &&
      wizData['configuration finding'] &&
      mainTabKeys.includes('DETECTION')
    ) {
      const configFinding = wizData['configuration finding'];
      sections['wiz_configuration'] = (
        <div className="space-y-4">
          <div className="border-warning border-l-4 p-4">
            <div className="flex items-center">
              <Cog8ToothIcon className="text-warning mr-2 size-5" />
              <h4 className="text-chariot-text-primary font-semibold">
                Configuration Issue
              </h4>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="border-chariot-stroke rounded-lg border p-4">
              <h5 className="text-chariot-text-primary mb-3 font-medium">
                Rule Details
              </h5>
              <div className="space-y-2 text-sm">
                {extractWizProperty(configFinding, 'configurationRuleName') && (
                  <div>
                    <strong>Rule:</strong>{' '}
                    {extractWizProperty(configFinding, 'configurationRuleName')}
                  </div>
                )}
                {extractWizProperty(
                  configFinding,
                  'configurationRuleShortName'
                ) && (
                  <div>
                    <strong>Rule ID:</strong>{' '}
                    <code>
                      {extractWizProperty(
                        configFinding,
                        'configurationRuleShortName'
                      )}
                    </code>
                  </div>
                )}
                {extractWizProperty(configFinding, 'severity') && (
                  <div>
                    <strong>Severity:</strong>{' '}
                    <span
                      className={getWizSeverityClass(
                        extractWizProperty(configFinding, 'severity')
                      )}
                    >
                      {extractWizProperty(configFinding, 'severity')}
                    </span>
                  </div>
                )}
                {extractWizProperty(configFinding, 'status') && (
                  <div>
                    <strong>Status:</strong>{' '}
                    <span className="text-error">
                      {extractWizProperty(configFinding, 'status')}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="border-chariot-stroke rounded-lg border p-4">
              <h5 className="text-chariot-text-primary mb-3 font-medium">
                Configuration
              </h5>
              <div className="space-y-2 text-sm">
                {extractWizProperty(configFinding, 'currentConfig') && (
                  <div>
                    <strong>Current:</strong>{' '}
                    <code className="bg-error-selected text-error rounded px-2 py-1 text-xs">
                      {extractWizProperty(configFinding, 'currentConfig')}
                    </code>
                  </div>
                )}
                {extractWizProperty(configFinding, 'expectedConfig') && (
                  <div>
                    <strong>Expected:</strong>{' '}
                    <span className="text-success">
                      {extractWizProperty(configFinding, 'expectedConfig')}
                    </span>
                  </div>
                )}
                {extractWizProperty(configFinding, 'cloudPlatform') && (
                  <div>
                    <strong>Platform:</strong>{' '}
                    {extractWizProperty(configFinding, 'cloudPlatform')}
                  </div>
                )}
                {extractWizProperty(configFinding, 'updatedAt') && (
                  <div>
                    <strong>Updated:</strong>{' '}
                    {formatWizTimestamp(
                      extractWizProperty(configFinding, 'updatedAt')
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          {extractWizProperty(configFinding, 'configLink') && (
            <div className="border-chariot-stroke rounded-lg border p-3">
              <a
                href={extractWizProperty(configFinding, 'configLink') || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="text-chariot-accent hover:text-chariot-accent-hover flex items-center text-sm"
              >
                <ArrowTopRightOnSquareIcon className="mr-1 size-4" />
                View Configuration Documentation
              </a>
            </div>
          )}
        </div>
      );
      tempTabs['DETECTION'].push({
        label: 'Configuration',
        value: 'wiz_configuration',
      });
    }

    // Process Simple Vulnerability Format (wiz_id, os, cvss_base, version, fixed_version)
    if (isSimpleFormat && mainTabKeys.includes('DETECTION')) {
      const getCvssColor = (score: string | number) => {
        const numScore = typeof score === 'string' ? parseFloat(score) : score;
        if (numScore >= 9.0) return 'text-error';
        if (numScore >= 7.0) return 'text-warning';
        if (numScore >= 4.0) return 'text-warning';
        return 'text-success';
      };

      const getCvssLabel = (score: string | number) => {
        const numScore = typeof score === 'string' ? parseFloat(score) : score;
        if (numScore >= 9.0) return 'Critical';
        if (numScore >= 7.0) return 'High';
        if (numScore >= 4.0) return 'Medium';
        return 'Low';
      };

      sections['wiz_simple_vuln'] = (
        <div className="space-y-4">
          <div className="border-error border-l-4 p-4">
            <div className="flex items-center">
              <ShieldExclamationIcon className="text-error mr-2 size-5" />
              <h4 className="text-chariot-text-primary font-semibold">
                Vulnerability Detection
              </h4>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {/* Vulnerability Details */}
            <div className="border-chariot-stroke rounded-lg border p-4">
              <h5 className="text-chariot-text-primary mb-3 font-medium">
                Vulnerability Details
              </h5>
              <div className="space-y-3">
                {wizData.cvss_base && (
                  <div className="flex items-start justify-between">
                    <span className="text-chariot-text-secondary font-medium">
                      CVSS Score:
                    </span>
                    <div className="text-right">
                      <span
                        className={`text-lg font-bold ${getCvssColor(wizData.cvss_base)}`}
                      >
                        {wizData.cvss_base}
                      </span>
                      <span
                        className={`ml-2 rounded px-2 py-1 text-xs ${getCvssColor(wizData.cvss_base)} bg-opacity-10`}
                      >
                        {getCvssLabel(wizData.cvss_base)}
                      </span>
                    </div>
                  </div>
                )}
                {wizData.os && (
                  <div className="flex items-start justify-between">
                    <span className="text-chariot-text-secondary font-medium">
                      Operating System:
                    </span>
                    <span className="text-chariot-text-primary font-mono">
                      {wizData.os}
                    </span>
                  </div>
                )}
                {wizId && (
                  <div className="flex items-start justify-between">
                    <span className="text-chariot-text-secondary font-medium">
                      Wiz ID:
                    </span>
                    <code className="bg-chariot-background-secondary rounded px-2 py-1 text-xs">
                      {wizId}
                    </code>
                  </div>
                )}
              </div>
            </div>

            {/* Version Information */}
            <div className="border-chariot-stroke rounded-lg border p-4">
              <h5 className="text-chariot-text-primary mb-3 font-medium">
                Version Information
              </h5>
              <div className="space-y-3">
                {wizData.version && (
                  <div>
                    <span className="text-chariot-text-secondary mb-1 block font-medium">
                      Current Version:
                    </span>
                    <code className="bg-error-selected text-error block rounded px-3 py-2 text-sm">
                      {wizData.version}
                    </code>
                  </div>
                )}
                {wizData.fixed_version && (
                  <div>
                    <span className="text-chariot-text-secondary mb-1 block font-medium">
                      Fixed Version:
                    </span>
                    <code className="bg-success-selected text-success block rounded px-3 py-2 text-sm">
                      {wizData.fixed_version}
                    </code>
                  </div>
                )}
                {wizData.version && wizData.fixed_version && (
                  <div className="border-chariot-stroke mt-3 border-t pt-3">
                    <div className="text-chariot-text-secondary flex items-center text-sm">
                      <InformationCircleIcon className="mr-2 size-4" />
                      <span>Update required to resolve vulnerability</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Action Required */}
          {wizData.version && wizData.fixed_version && (
            <div className="border-warning border-l-4 p-4">
              <div className="flex items-start">
                <ExclamationTriangleIcon className="text-warning mt-0.5 mr-3 size-5 shrink-0" />
                <div>
                  <h5 className="text-chariot-text-primary mb-2 font-semibold">
                    Action Required
                  </h5>
                  <p className="text-chariot-text-primary text-sm">
                    This system is running <strong>{wizData.version}</strong>{' '}
                    which contains known vulnerabilities. Update to{' '}
                    <strong>{wizData.fixed_version}</strong> or later to resolve
                    this security issue.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      );
      tempTabs['DETECTION'].push({
        label: 'Vulnerability',
        value: 'wiz_simple_vuln',
      });
    }

    // Create simple attributes section for simple format
    if (isSimpleFormat && mainTabKeys.includes('ATTRIBUTES')) {
      sections['wiz_simple_attributes'] = (
        <div className="space-y-4">
          <div className="border-chariot-stroke rounded-lg border">
            <div className="border-chariot-stroke border-b px-4 py-3">
              <h4 className="text-chariot-text-primary font-semibold">
                Cloud Resource
              </h4>
              <span className="border-chariot-stroke bg-chariot-background-secondary text-chariot-text-primary mt-2 inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium">
                Wiz ID: {wizId}
              </span>
            </div>
          </div>

          <div className="border-chariot-stroke rounded-lg border">
            <div className="border-chariot-stroke border-b px-4 py-3">
              <h4 className="text-chariot-text-primary font-semibold">
                Additional Properties
              </h4>
            </div>
            <div className="space-y-3 p-4">
              {wizData.os && (
                <div className="border-chariot-stroke flex items-start justify-between border-b py-2 last:border-b-0">
                  <span className="text-chariot-text-secondary font-medium">
                    OS:
                  </span>
                  <span className="text-chariot-text-primary">
                    {wizData.os}
                  </span>
                </div>
              )}
              {wizData.cvss_base && (
                <div className="border-chariot-stroke flex items-start justify-between border-b py-2 last:border-b-0">
                  <span className="text-chariot-text-secondary font-medium">
                    Cvss Base:
                  </span>
                  <span className="text-chariot-text-primary">
                    {wizData.cvss_base}
                  </span>
                </div>
              )}
              {wizData.version && (
                <div className="border-chariot-stroke flex items-start justify-between border-b py-2 last:border-b-0">
                  <span className="text-chariot-text-secondary font-medium">
                    Version:
                  </span>
                  <span className="text-chariot-text-primary">
                    {wizData.version}
                  </span>
                </div>
              )}
              {wizData.fixed_version && (
                <div className="border-chariot-stroke flex items-start justify-between border-b py-2 last:border-b-0">
                  <span className="text-chariot-text-secondary font-medium">
                    Fixed Version:
                  </span>
                  <span className="text-chariot-text-primary">
                    {wizData.fixed_version}
                  </span>
                </div>
              )}
              {/* Show any other properties that might exist */}
              {Object.entries(wizData)
                .filter(
                  ([key]) =>
                    ![
                      'wiz_id',
                      'os',
                      'cvss_base',
                      'version',
                      'fixed_version',
                    ].includes(key)
                )
                .map(([key, value]) => (
                  <div
                    key={key}
                    className="border-chariot-stroke flex items-start justify-between border-b py-2 last:border-b-0"
                  >
                    <span className="text-chariot-text-secondary font-medium">
                      {formatKey(key)}:
                    </span>
                    <span className="text-chariot-text-primary">
                      {String(value)}
                    </span>
                  </div>
                ))}
            </div>
          </div>
        </div>
      );
      tempTabs['ATTRIBUTES'].push({
        label: 'Resource Details',
        value: 'wiz_simple_attributes',
      });
    }

    // Create subscription section for subscription format
    if (isSubscriptionFormat && mainTabKeys.includes('ATTRIBUTES')) {
      const subscriptionData = parseWizFinding(wizData.subscription);

      sections['wiz_subscription'] = (
        <div className="space-y-4">
          <div className="border-chariot-stroke rounded-lg border">
            <div className="border-chariot-stroke border-b px-4 py-3">
              <h4 className="text-chariot-text-primary font-semibold">
                Cloud Resource
              </h4>
              <span className="border-chariot-stroke bg-chariot-background-secondary text-chariot-text-primary mt-2 inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium">
                Wiz ID: {wizId}
              </span>
            </div>
          </div>

          <div className="border-chariot-stroke rounded-lg border">
            <div className="border-chariot-stroke border-b px-4 py-3">
              <h4 className="text-chariot-text-primary font-semibold">
                AWS Account Details
              </h4>
            </div>
            <div className="space-y-3 p-4">
              {subscriptionData.name && (
                <div className="border-chariot-stroke flex items-start justify-between border-b py-2 last:border-b-0">
                  <span className="text-chariot-text-secondary font-medium">
                    Account Name:
                  </span>
                  <span className="text-chariot-text-primary font-medium">
                    {subscriptionData.name}
                  </span>
                </div>
              )}
              {subscriptionData.properties.subscriptionId && (
                <div className="border-chariot-stroke flex items-start justify-between border-b py-2 last:border-b-0">
                  <span className="text-chariot-text-secondary font-medium">
                    Account ID:
                  </span>
                  <code className="bg-chariot-background-secondary text-chariot-text-primary rounded px-2 py-1 text-sm">
                    {subscriptionData.properties.subscriptionId}
                  </code>
                </div>
              )}
              {subscriptionData.properties.status && (
                <div className="border-chariot-stroke flex items-start justify-between border-b py-2 last:border-b-0">
                  <span className="text-chariot-text-secondary font-medium">
                    Status:
                  </span>
                  <span
                    className={`rounded px-2 py-1 text-sm font-medium ${
                      subscriptionData.properties.status === 'Active'
                        ? 'bg-success-selected text-success'
                        : 'text-chariot-text-primary'
                    }`}
                  >
                    {subscriptionData.properties.status}
                  </span>
                </div>
              )}
              {subscriptionData.properties.cloudPlatform && (
                <div className="border-chariot-stroke flex items-start justify-between border-b py-2 last:border-b-0">
                  <span className="text-chariot-text-secondary font-medium">
                    Platform:
                  </span>
                  <span className="text-chariot-text-primary">
                    {subscriptionData.properties.cloudPlatform}
                  </span>
                </div>
              )}
              {subscriptionData.properties.creationDate && (
                <div className="border-chariot-stroke flex items-start justify-between border-b py-2 last:border-b-0">
                  <span className="text-chariot-text-secondary font-medium">
                    Created:
                  </span>
                  <span className="text-chariot-text-primary text-sm">
                    {formatWizTimestamp(
                      subscriptionData.properties.creationDate
                    )}
                  </span>
                </div>
              )}
              {subscriptionData.properties.updatedAt && (
                <div className="border-chariot-stroke flex items-start justify-between border-b py-2 last:border-b-0">
                  <span className="text-chariot-text-secondary font-medium">
                    Last Updated:
                  </span>
                  <span className="text-chariot-text-primary text-sm">
                    {formatWizTimestamp(subscriptionData.properties.updatedAt)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {subscriptionData.properties.path && (
            <div className="border-chariot-stroke rounded-lg border">
              <div className="border-chariot-stroke border-b px-4 py-3">
                <h4 className="text-chariot-text-primary font-semibold">
                  Organization Path
                </h4>
              </div>
              <div className="p-4">
                <code className="bg-chariot-background-secondary text-chariot-text-primary block overflow-x-auto rounded p-3 font-mono text-xs break-all">
                  {subscriptionData.properties.path}
                </code>
              </div>
            </div>
          )}

          {subscriptionData.providerURL && (
            <div className="border-chariot-stroke rounded-lg border p-4">
              <div className="flex items-center justify-between">
                <span className="text-chariot-text-secondary font-medium">
                  AWS Console:
                </span>
                <a
                  href={subscriptionData.providerURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-chariot-accent hover:text-chariot-accent-hover flex items-center gap-1 text-sm"
                >
                  Open Account Console
                  <ArrowTopRightOnSquareIcon className="size-3" />
                </a>
              </div>
            </div>
          )}
        </div>
      );
      tempTabs['ATTRIBUTES'].push({
        label: 'Subscription Details',
        value: 'wiz_subscription',
      });
    }

    // Process Endpoint Information (from proofs files) - only for simple endpoint format, not complex endpoint+vulnerability format
    if (
      wizData &&
      wizData['endpoint'] &&
      !wizData['security tool finding'] &&
      !wizData['vulnerability'] &&
      mainTabKeys.includes('ATTRIBUTES')
    ) {
      const endpoint = wizData['endpoint'];

      // Parse all endpoint properties
      const endpointData =
        typeof endpoint === 'string' ? parseWizFinding(endpoint) : endpoint;
      const endpointName =
        extractWizProperty(endpointData, 'name') ||
        extractWizProperty(endpointData, 'Name') ||
        '';
      const endpointType =
        extractWizProperty(endpointData, 'type') ||
        extractWizProperty(endpointData, 'Type') ||
        '';
      const cloudPlatform =
        extractWizProperty(endpointData, 'cloudPlatform') ||
        extractWizProperty(endpointData, 'Cloud Platform') ||
        '';
      const protocols =
        extractWizProperty(endpointData, 'protocols') ||
        extractWizProperty(endpointData, 'Protocols') ||
        '';
      const httpStatus =
        extractWizProperty(endpointData, 'httpGETStatus') || '';
      const exposureLevel =
        extractWizProperty(endpointData, 'exposureLevel_name') ||
        extractWizProperty(endpointData, 'Exposure Level Name') ||
        '';
      const exposureDescription =
        extractWizProperty(endpointData, 'exposureLevel_description') || '';
      const host =
        extractWizProperty(endpointData, 'host') ||
        extractWizProperty(endpointData, 'finalHost') ||
        '';
      const path = extractWizProperty(endpointData, 'path') || '';
      const subscriptionId =
        extractWizProperty(endpointData, 'subscriptionExternalId') || '';
      const externalId = extractWizProperty(endpointData, 'externalId') || '';
      const updatedAt = extractWizProperty(endpointData, 'updatedAt') || '';
      const productIds = extractWizProperty(endpointData, 'productIDs') || '';

      sections['wiz_endpoint'] = (
        <div className="space-y-4">
          {/* Endpoint Details */}
          <div className="border-chariot-stroke rounded-lg border">
            <div className="border-chariot-stroke border-b px-4 py-3">
              <h4 className="text-chariot-text-primary font-semibold">
                Endpoint Details
              </h4>
            </div>
            <div className="space-y-3 p-4">
              {endpointType && (
                <div className="border-chariot-stroke flex items-center justify-between border-b py-2 last:border-b-0">
                  <span className="text-chariot-text-secondary font-medium">
                    Type:
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-chariot-text-primary">
                      {endpointType}
                    </span>
                    <CopyToClipboard
                      textToCopy={endpointType}
                      alwaysShowCopyIcon
                    />
                  </div>
                </div>
              )}
              {endpointName && (
                <div className="border-chariot-stroke flex items-center justify-between border-b py-2 last:border-b-0">
                  <span className="text-chariot-text-secondary font-medium">
                    Name:
                  </span>
                  <div className="flex max-w-md items-center gap-2">
                    <span className="text-chariot-text-primary text-right font-mono text-sm break-all">
                      {endpointName}
                    </span>
                    <CopyToClipboard
                      textToCopy={endpointName}
                      alwaysShowCopyIcon
                    />
                  </div>
                </div>
              )}
              {host && (
                <div className="border-chariot-stroke flex items-center justify-between border-b py-2 last:border-b-0">
                  <span className="text-chariot-text-secondary font-medium">
                    Host:
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-chariot-text-primary">{host}</span>
                    <CopyToClipboard textToCopy={host} alwaysShowCopyIcon />
                  </div>
                </div>
              )}
              {path && (
                <div className="border-chariot-stroke flex items-center justify-between border-b py-2 last:border-b-0">
                  <span className="text-chariot-text-secondary font-medium">
                    Path:
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-chariot-text-primary font-mono">
                      {path}
                    </span>
                    <CopyToClipboard textToCopy={path} alwaysShowCopyIcon />
                  </div>
                </div>
              )}
              {protocols && (
                <div className="border-chariot-stroke flex items-center justify-between border-b py-2 last:border-b-0">
                  <span className="text-chariot-text-secondary font-medium">
                    Protocols:
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-chariot-text-primary">
                      {protocols}
                    </span>
                    <CopyToClipboard
                      textToCopy={protocols}
                      alwaysShowCopyIcon
                    />
                  </div>
                </div>
              )}
              {httpStatus && (
                <div className="border-chariot-stroke flex items-center justify-between border-b py-2 last:border-b-0">
                  <span className="text-chariot-text-secondary font-medium">
                    HTTP Status:
                  </span>
                  <div className="flex items-center gap-2">
                    <span
                      className={`font-medium ${getHttpStatusClass(httpStatus)}`}
                    >
                      {httpStatus}
                    </span>
                    <CopyToClipboard
                      textToCopy={httpStatus}
                      alwaysShowCopyIcon
                    />
                  </div>
                </div>
              )}
              {cloudPlatform && (
                <div className="border-chariot-stroke flex items-center justify-between border-b py-2 last:border-b-0">
                  <span className="text-chariot-text-secondary font-medium">
                    Cloud Platform:
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-chariot-text-primary">
                      {cloudPlatform}
                    </span>
                    <CopyToClipboard
                      textToCopy={cloudPlatform}
                      alwaysShowCopyIcon
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Security & Exposure */}
          {(exposureLevel || exposureDescription) && (
            <div className="border-chariot-stroke rounded-lg border">
              <div className="border-chariot-stroke border-b px-4 py-3">
                <h4 className="text-chariot-text-primary font-semibold">
                  Security & Exposure
                </h4>
              </div>
              <div className="space-y-3 p-4">
                {exposureLevel && (
                  <div className="border-chariot-stroke flex items-center justify-between border-b py-2 last:border-b-0">
                    <span className="text-chariot-text-secondary font-medium">
                      Exposure Level:
                    </span>
                    <div className="flex items-center gap-2">
                      <span
                        className={`font-medium ${getExposureLevelClass(exposureLevel)}`}
                      >
                        {exposureLevel}
                      </span>
                      <CopyToClipboard
                        textToCopy={exposureLevel}
                        alwaysShowCopyIcon
                      />
                    </div>
                  </div>
                )}
                {exposureDescription && (
                  <div className="py-2">
                    <div className="mb-2 flex items-start justify-between">
                      <span className="text-chariot-text-secondary font-medium">
                        Exposure Description:
                      </span>
                      <CopyToClipboard
                        textToCopy={exposureDescription}
                        alwaysShowCopyIcon
                      />
                    </div>
                    <p className="text-chariot-text-primary text-sm">
                      {exposureDescription}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Additional Properties */}
          {(subscriptionId || externalId || updatedAt || productIds) && (
            <div className="border-chariot-stroke rounded-lg border">
              <div className="border-chariot-stroke border-b px-4 py-3">
                <h4 className="text-chariot-text-primary font-semibold">
                  Additional Properties
                </h4>
              </div>
              <div className="space-y-3 p-4">
                {subscriptionId && (
                  <div className="border-chariot-stroke flex items-center justify-between border-b py-2 last:border-b-0">
                    <span className="text-chariot-text-secondary font-medium">
                      Subscription ID:
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-chariot-text-primary font-mono">
                        {subscriptionId}
                      </span>
                      <CopyToClipboard
                        textToCopy={subscriptionId}
                        alwaysShowCopyIcon
                      />
                    </div>
                  </div>
                )}
                {externalId && (
                  <div className="border-chariot-stroke flex items-center justify-between border-b py-2 last:border-b-0">
                    <span className="text-chariot-text-secondary font-medium">
                      External ID:
                    </span>
                    <div className="flex max-w-md items-center gap-2">
                      <span className="text-chariot-text-primary text-right font-mono text-sm break-all">
                        {externalId}
                      </span>
                      <CopyToClipboard
                        textToCopy={externalId}
                        alwaysShowCopyIcon
                      />
                    </div>
                  </div>
                )}
                {updatedAt && (
                  <div className="border-chariot-stroke flex items-center justify-between border-b py-2 last:border-b-0">
                    <span className="text-chariot-text-secondary font-medium">
                      Updated:
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-chariot-text-primary">
                        {formatWizTimestamp(updatedAt)}
                      </span>
                      <CopyToClipboard
                        textToCopy={updatedAt}
                        alwaysShowCopyIcon
                      />
                    </div>
                  </div>
                )}
                {productIds && (
                  <div className="border-chariot-stroke flex items-center justify-between border-b py-2 last:border-b-0">
                    <span className="text-chariot-text-secondary font-medium">
                      Product IDs:
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-chariot-text-primary font-mono text-sm">
                        {productIds}
                      </span>
                      <CopyToClipboard
                        textToCopy={productIds}
                        alwaysShowCopyIcon
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      );
      tempTabs['ATTRIBUTES'].push({ label: 'Endpoint', value: 'wiz_endpoint' });
    }

    // Create vulnerability description section
    if (vulnerabilityDescription && mainTabKeys.includes('INFORMATION')) {
      sections['wiz_vulnerability'] = (
        <div className="space-y-4">
          <div className="border-warning border-l-4 p-4">
            <div className="flex items-center">
              <ShieldExclamationIcon className="text-warning mr-2 size-5" />
              <h4 className="text-chariot-text-primary font-semibold">
                Wiz Security Finding
              </h4>
            </div>
          </div>
          <div className="prose max-w-none">
            <div className="border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary rounded border p-4 text-sm leading-relaxed whitespace-pre-wrap">
              <MarkdownPreview source={vulnerabilityDescription} />
            </div>
          </div>
        </div>
      );
      tempTabs['INFORMATION'].push({
        label: 'Vulnerability Details',
        value: 'wiz_vulnerability',
      });
    }

    // Create resource details section (only for non-simple and non-subscription format)
    if (
      resourceData &&
      !isSimpleFormat &&
      !isSubscriptionFormat &&
      mainTabKeys.includes('ATTRIBUTES')
    ) {
      sections['wiz_resource'] = (
        <div className="space-y-4">
          {wizId && (
            <div className="border-chariot-stroke rounded-lg border p-4">
              <div className="flex items-center justify-between">
                <h4 className="text-chariot-text-primary font-semibold">
                  Cloud Resource
                </h4>
                <span className="border-chariot-stroke bg-chariot-background-secondary text-chariot-text-primary inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium">
                  Wiz ID: {wizId}
                </span>
              </div>
            </div>
          )}

          {resourceData?.subscription && (
            <div className="border-chariot-stroke rounded-lg border">
              <div className="border-chariot-stroke border-b px-4 py-3">
                <h5 className="text-chariot-text-primary font-semibold">
                  Subscription Details
                </h5>
              </div>
              <div className="space-y-3 p-4">
                {(() => {
                  const subscription = resourceData.subscription;
                  if (typeof subscription === 'string') {
                    // Parse the subscription string into key-value pairs
                    const lines = subscription
                      .split('\n')
                      .filter(line => line.trim());
                    const parsed: Record<string, string> = {};

                    for (const line of lines) {
                      if (line.includes(':')) {
                        const [key, ...valueParts] = line.split(':');
                        const value = valueParts.join(':').trim();
                        if (key && value) {
                          parsed[key.trim()] = value;
                        }
                      } else if (line.startsWith('•')) {
                        const [key, ...valueParts] = line
                          .substring(1)
                          .split(':');
                        const value = valueParts.join(':').trim();
                        if (key && value) {
                          parsed[key.trim()] = value;
                        }
                      }
                    }

                    return Object.entries(parsed).length > 0 ? (
                      <>
                        {Object.entries(parsed).map(([key, value]) => (
                          <div
                            key={key}
                            className="border-chariot-stroke flex items-center justify-between border-b py-2 last:border-b-0"
                          >
                            <span className="text-chariot-text-secondary text-sm font-medium">
                              {formatKey(key)}:
                            </span>
                            <div className="flex max-w-md items-center gap-2">
                              <span className="text-chariot-text-primary text-right text-sm break-words">
                                {key.toLowerCase().includes('url') ? (
                                  <a
                                    href={value}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-chariot-brand-primary hover:underline"
                                  >
                                    {value}
                                  </a>
                                ) : (
                                  value
                                )}
                              </span>
                              <CopyToClipboard
                                textToCopy={value}
                                alwaysShowCopyIcon
                              />
                            </div>
                          </div>
                        ))}
                      </>
                    ) : (
                      <div className="text-chariot-text-secondary text-sm">
                        {subscription}
                      </div>
                    );
                  } else {
                    return (
                      <div className="text-chariot-text-secondary text-sm">
                        {JSON.stringify(subscription, null, 2)}
                      </div>
                    );
                  }
                })()}
              </div>
            </div>
          )}

          {resourceData &&
            Object.keys(resourceData).filter(
              key => key !== 'subscription' && key !== 'wiz_id'
            ).length > 0 && (
              <div className="border-chariot-stroke rounded-lg border">
                <div className="border-chariot-stroke border-b px-4 py-2">
                  <h5 className="text-chariot-text-primary font-semibold">
                    Additional Properties
                  </h5>
                </div>
                <div className="space-y-3 p-4">
                  {resourceData &&
                    Object.entries(resourceData)
                      .filter(
                        ([key]) => key !== 'subscription' && key !== 'wiz_id'
                      )
                      .map(([key, value]) => {
                        // Handle different value types for clean display
                        const formatValue = (
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          val: any,
                          propKey: string
                        ): React.ReactNode => {
                          if (typeof val === 'object' && val !== null) {
                            // For objects, return each property as a separate row instead of grouped
                            return null; // We'll handle this in the main loop
                          } else if (
                            typeof val === 'string' &&
                            (val.includes('\n') || val.includes('•'))
                          ) {
                            // Parse structured string data
                            const lines = val
                              .split('\n')
                              .filter(line => line.trim());
                            const hasStructuredData = lines.some(
                              line => line.includes(':') || line.startsWith('•')
                            );

                            if (hasStructuredData) {
                              return null; // We'll handle this in the main loop
                            }
                            return (
                              <span className="text-chariot-text-primary whitespace-pre-line">
                                {val}
                              </span>
                            );
                          } else if (
                            typeof val === 'string' &&
                            val.length > 100
                          ) {
                            // For long strings, use proper formatting
                            return (
                              <span className="text-chariot-text-primary max-w-md text-right break-all">
                                {val}
                              </span>
                            );
                          } else {
                            // For simple values, check if it's a URL
                            const strValue = String(val);
                            if (
                              propKey.toLowerCase().includes('url') ||
                              strValue.startsWith('http')
                            ) {
                              return (
                                <a
                                  href={strValue}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-chariot-brand-primary max-w-md text-right break-all hover:underline"
                                >
                                  {strValue}
                                </a>
                              );
                            }
                            return (
                              <span className="text-chariot-text-primary">
                                {strValue}
                              </span>
                            );
                          }
                        };

                        // Handle structured data by expanding it into individual rows
                        if (typeof value === 'object' && value !== null) {
                          // For objects, create individual rows for each sub-property
                          return Object.entries(value).map(
                            ([subKey, subValue]) => (
                              <div
                                key={`${key}-${subKey}`}
                                className="border-chariot-stroke flex items-center justify-between border-b py-2 last:border-b-0"
                              >
                                <span className="text-chariot-text-secondary font-medium">
                                  {formatKey(subKey)}:
                                </span>
                                <div className="flex max-w-md items-center gap-2">
                                  <span className="text-chariot-text-primary text-right">
                                    {String(subValue)}
                                  </span>
                                  <CopyToClipboard
                                    textToCopy={String(subValue)}
                                    alwaysShowCopyIcon
                                  />
                                </div>
                              </div>
                            )
                          );
                        } else if (
                          typeof value === 'string' &&
                          (value.includes('\n') || value.includes('•'))
                        ) {
                          // Parse structured string data into individual rows
                          const lines = value
                            .split('\n')
                            .filter(line => line.trim());
                          const hasStructuredData = lines.some(
                            line => line.includes(':') || line.startsWith('•')
                          );

                          if (hasStructuredData) {
                            const parsed: Record<string, string> = {};
                            for (const line of lines) {
                              if (line.includes(':')) {
                                const [lineKey, ...valueParts] =
                                  line.split(':');
                                const lineValue = valueParts.join(':').trim();
                                if (lineKey && lineValue) {
                                  parsed[lineKey.trim().replace(/^•\s*/, '')] =
                                    lineValue;
                                }
                              }
                            }

                            if (Object.entries(parsed).length > 0) {
                              return Object.entries(parsed).map(
                                ([lineKey, lineValue]) => (
                                  <div
                                    key={`${key}-${lineKey}`}
                                    className="border-chariot-stroke flex items-center justify-between border-b py-2 last:border-b-0"
                                  >
                                    <span className="text-chariot-text-secondary font-medium">
                                      {formatKey(lineKey)}:
                                    </span>
                                    <div className="flex max-w-md items-center gap-2">
                                      <span className="text-chariot-text-primary overflow-hidden text-right">
                                        {lineKey
                                          .toLowerCase()
                                          .includes('url') ? (
                                          <a
                                            href={lineValue}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-chariot-brand-primary break-all hover:underline"
                                          >
                                            {lineValue}
                                          </a>
                                        ) : (
                                          lineValue
                                        )}
                                      </span>
                                      <CopyToClipboard
                                        textToCopy={lineValue}
                                        alwaysShowCopyIcon
                                      />
                                    </div>
                                  </div>
                                )
                              );
                            }
                          }
                          // Fall back to single row for unstructured multi-line text
                          return (
                            <div
                              key={key}
                              className="border-chariot-stroke flex items-center justify-between border-b py-2 last:border-b-0"
                            >
                              <span className="text-chariot-text-secondary font-medium">
                                {formatKey(key)}:
                              </span>
                              <div className="flex max-w-md items-center gap-2">
                                <span className="text-chariot-text-primary text-right whitespace-pre-line">
                                  {value}
                                </span>
                                <CopyToClipboard
                                  textToCopy={value}
                                  alwaysShowCopyIcon
                                />
                              </div>
                            </div>
                          );
                        } else {
                          // Simple single row
                          return (
                            <div
                              key={key}
                              className="border-chariot-stroke flex items-center justify-between border-b py-2 last:border-b-0"
                            >
                              <span className="text-chariot-text-secondary font-medium">
                                {formatKey(key)}:
                              </span>
                              <div className="flex max-w-md items-center gap-2 text-right">
                                {formatValue(value, key)}
                                <CopyToClipboard
                                  textToCopy={String(value)}
                                  alwaysShowCopyIcon
                                />
                              </div>
                            </div>
                          );
                        }
                      })}
                </div>
              </div>
            )}
        </div>
      );
      tempTabs['ATTRIBUTES'].push({
        label: 'Resource Details',
        value: 'wiz_resource',
      });
    }

    // Create detection/finding section
    if (
      (vulnerabilityDescription || resourceData) &&
      mainTabKeys.includes('DETECTION')
    ) {
      sections['wiz_finding'] = (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="rounded-lg p-4">
              <h4 className="text-chariot-text-primary mb-2 font-semibold">
                Finding Summary
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-chariot-text-secondary">Source:</span>
                  <span className="text-chariot-accent font-medium">
                    Wiz.io
                  </span>
                </div>
                {wizId && (
                  <div className="flex justify-between">
                    <span className="text-chariot-text-secondary">
                      Finding ID:
                    </span>
                    <code className="bg-chariot-background-secondary rounded px-2 py-1 text-xs">
                      {wizId}
                    </code>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-chariot-text-secondary">
                    Evidence Type:
                  </span>
                  <span className="font-medium">Vulnerability Assessment</span>
                </div>
              </div>
            </div>

            {resourceData && (
              <div className="rounded-lg p-4">
                <h4 className="text-chariot-text-primary mb-2 font-semibold">
                  Asset Information
                </h4>
                <div className="space-y-2 text-sm">
                  {resourceData?.subscription && (
                    <div>
                      <span className="text-chariot-text-secondary block">
                        Resource Type:
                      </span>
                      <span className="font-medium">Cloud Subscription</span>
                    </div>
                  )}
                  <div>
                    <span className="text-chariot-text-secondary block">
                      Platform:
                    </span>
                    <span className="font-medium">
                      {resourceData?.subscription?.includes('AWS')
                        ? 'Amazon Web Services'
                        : resourceData?.subscription?.includes('Azure')
                          ? 'Microsoft Azure'
                          : resourceData?.subscription?.includes('GCP')
                            ? 'Google Cloud Platform'
                            : 'Cloud Platform'}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {vulnerabilityDescription && (
            <div className="border-error-light bg-error-selected rounded-lg border">
              <div className="border-error-light bg-error-light border-b px-4 py-2">
                <h5 className="text-error flex items-center font-semibold">
                  <InformationCircleIcon className="mr-2 size-4" />
                  Security Impact
                </h5>
              </div>
              <div className="p-4">
                <p className="text-error text-sm leading-relaxed">
                  {vulnerabilityDescription.length > 200
                    ? `${vulnerabilityDescription.substring(0, 200)}...`
                    : vulnerabilityDescription}
                </p>
                {vulnerabilityDescription.length > 200 && (
                  <button className="text-chariot-accent hover:text-chariot-accent-hover mt-2 text-xs">
                    View full description in Information tab →
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      );
      tempTabs['DETECTION'].push({
        label: 'Wiz Finding',
        value: 'wiz_finding',
      });
    }

    // Security Impact Analysis - This is what the user will be proud of!
    if (
      wizData?.['configuration finding'] &&
      wizData?.['endpoint'] &&
      mainTabKeys.includes('INFORMATION')
    ) {
      const configFinding = wizData['configuration finding'];
      const endpoint = wizData['endpoint'];
      // const severity =
      //   extractWizProperty(configFinding, 'severity')?.replace(
      //     'ConfigurationSeverity',
      //     ''
      //   ) || 'Unknown';
      const currentRuntime =
        extractWizProperty(configFinding, 'currentConfig') || '';
      const endpointName = extractWizProperty(endpoint, 'name') || '';
      // const exposureLevel =
      //   extractWizProperty(endpoint, 'exposureLevel_name') || '';

      sections['wiz_security_impact'] = (
        <div className="space-y-6">
          {/* Risk Overview */}
          <div className="border-warning rounded-r-lg border-l-4 p-4">
            <div className="flex items-start">
              <ShieldExclamationIcon className="text-warning mt-1 mr-3 size-6 shrink-0" />
              <div>
                <h4 className="text-chariot-text-primary mb-2 text-lg font-semibold">
                  Security Risk Assessment
                </h4>
                <p className="text-chariot-text-primary leading-relaxed">
                  This AWS Lambda function is running{' '}
                  <strong>{currentRuntime}</strong>, which has reached
                  end-of-life and no longer receives security patches. The
                  function is publicly accessible via API Gateway, creating a
                  potential attack vector for exploiting deprecated runtime
                  vulnerabilities.
                </p>
              </div>
            </div>
          </div>

          {/* Impact Categories */}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* Technical Impact */}
            <div className="border-chariot-stroke rounded-lg border">
              <div className="border-chariot-stroke border-b px-4 py-2">
                <h5 className="text-error flex items-center font-semibold">
                  <ExclamationTriangleIcon className="mr-2 size-4" />
                  Technical Impact
                </h5>
              </div>
              <div className="space-y-3 p-4">
                <div className="flex items-start gap-3">
                  <span className="bg-error mt-2 size-2 shrink-0 rounded-full"></span>
                  <div>
                    <strong className="text-chariot-text-primary text-sm">
                      Deprecated Runtime Vulnerabilities
                    </strong>
                    <p className="text-chariot-text-secondary mt-1 text-xs">
                      Known CVEs in Python 3.6 will not be patched
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="bg-error mt-2 size-2 shrink-0 rounded-full"></span>
                  <div>
                    <strong className="text-chariot-text-primary text-sm">
                      Public API Exposure
                    </strong>
                    <p className="text-chariot-text-secondary mt-1 text-xs">
                      Function accessible via {endpointName}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="bg-warning mt-2 size-2 shrink-0 rounded-full"></span>
                  <div>
                    <strong className="text-chariot-text-primary text-sm">
                      Compliance Risk
                    </strong>
                    <p className="text-chariot-text-secondary mt-1 text-xs">
                      Violates security best practices for runtime management
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Business Impact */}
            <div className="border-chariot-stroke rounded-lg border">
              <div className="border-chariot-stroke border-b px-4 py-2">
                <h5 className="text-warning flex items-center font-semibold">
                  <InformationCircleIcon className="mr-2 size-4" />
                  Business Impact
                </h5>
              </div>
              <div className="space-y-3 p-4">
                <div className="flex items-start gap-3">
                  <span className="bg-error mt-2 size-2 shrink-0 rounded-full"></span>
                  <div>
                    <strong className="text-chariot-text-primary text-sm">
                      Data Breach Risk
                    </strong>
                    <p className="text-chariot-text-secondary mt-1 text-xs">
                      Potential unauthorized access to function data and secrets
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="bg-warning mt-2 size-2 shrink-0 rounded-full"></span>
                  <div>
                    <strong className="text-chariot-text-primary text-sm">
                      Service Disruption
                    </strong>
                    <p className="text-chariot-text-secondary mt-1 text-xs">
                      Potential denial of service or function compromise
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="bg-warning mt-2 size-2 shrink-0 rounded-full"></span>
                  <div>
                    <strong className="text-chariot-text-primary text-sm">
                      Reputational Impact
                    </strong>
                    <p className="text-chariot-text-secondary mt-1 text-xs">
                      Security incidents from deprecated components
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Remediation Steps */}
          <div className="border-success rounded-r-lg border-l-4 p-4">
            <div className="flex items-start">
              <CheckCircleIcon className="text-success mt-1 mr-3 size-6 shrink-0" />
              <div className="grow">
                <h4 className="text-chariot-text-primary mb-3 text-lg font-semibold">
                  Recommended Actions
                </h4>
                <div className="space-y-2">
                  <div className="flex items-start gap-3">
                    <span className="bg-success inline-flex size-6 items-center justify-center rounded-full text-xs font-bold text-white">
                      1
                    </span>
                    <div>
                      <strong className="text-chariot-text-primary">
                        Update Runtime Immediately
                      </strong>
                      <p className="text-chariot-text-secondary text-sm">
                        Upgrade to Python 3.9+ which has active security support
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="bg-success inline-flex size-6 items-center justify-center rounded-full text-xs font-bold text-white">
                      2
                    </span>
                    <div>
                      <strong className="text-chariot-text-primary">
                        Test Function Compatibility
                      </strong>
                      <p className="text-chariot-text-secondary text-sm">
                        Ensure application works correctly with updated runtime
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="bg-success inline-flex size-6 items-center justify-center rounded-full text-xs font-bold text-white">
                      3
                    </span>
                    <div>
                      <strong className="text-chariot-text-primary">
                        Review Access Controls
                      </strong>
                      <p className="text-chariot-text-secondary text-sm">
                        Verify API Gateway permissions and authentication
                        requirements
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
      tempTabs['INFORMATION'].push({
        label: 'Security Impact',
        value: 'wiz_security_impact',
      });
    }

    // Process Endpoint + Security Tool Finding + Vulnerability Format
    if (
      wizData &&
      wizData['endpoint'] &&
      wizData['security tool finding'] &&
      wizData['vulnerability'] &&
      mainTabKeys.includes('DETECTION')
    ) {
      const endpoint = wizData['endpoint'];
      const securityTool = wizData['security tool finding'];
      const vulnerability = wizData['vulnerability'];

      // Extract endpoint data
      const endpointName = extractWizProperty(endpoint, 'name') || '';
      const endpointPath = extractWizProperty(endpoint, 'path') || '';
      const httpStatus = extractWizProperty(endpoint, 'httpGETStatus') || '';
      const exposureLevel =
        extractWizProperty(endpoint, 'exposureLevel_name') || '';
      const exposureDescription =
        extractWizProperty(endpoint, 'exposureLevel_description') || '';
      const cloudPlatform = extractWizProperty(endpoint, 'cloudPlatform') || '';
      const protocols = extractWizProperty(endpoint, 'protocols') || '';

      // Extract vulnerability data
      const vulnName = extractWizProperty(vulnerability, 'name') || '';
      const cveId = vulnName || extractWizProperty(vulnerability, 'cve') || '';
      const vulnDescription =
        extractWizProperty(vulnerability, 'description') || '';
      const severity =
        extractWizProperty(vulnerability, 'severity')?.replace(
          'VulnerabilitySeverity',
          ''
        ) || '';
      const publishedDate =
        extractWizProperty(vulnerability, 'publishedDate') || '';

      // Extract security tool data
      const detectedLibrary =
        extractWizProperty(securityTool, 'detectedByLibraryName') ||
        extractWizProperty(securityTool, 'detailed_name') ||
        '';
      const currentVersion =
        extractWizProperty(securityTool, 'detailed_version') || '';
      const fixedVersion =
        extractWizProperty(securityTool, 'detailed_fixedVersion') || '';
      const filePath =
        extractWizProperty(securityTool, 'detailed_filePath') ||
        extractWizProperty(securityTool, 'detectedByFilePath') ||
        '';
      const remediation = extractWizProperty(securityTool, 'remediation') || '';
      const language =
        extractWizProperty(securityTool, 'detailed_language') || '';

      sections['wiz_endpoint_vuln'] = (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {/* API Endpoint Information */}
            <div className="border-chariot-stroke rounded-lg border">
              <div className="border-chariot-stroke border-b px-4 py-3">
                <h4 className="text-chariot-text-primary flex items-center font-semibold">
                  <LinkIcon className="text-chariot-brand-primary mr-2 size-4" />
                  API Endpoint
                </h4>
              </div>
              <div className="space-y-3 p-4">
                <div>
                  <span className="text-chariot-text-secondary mb-1 block text-xs font-medium">
                    URL:
                  </span>
                  <code className="bg-chariot-background-secondary block rounded px-2 py-1 text-xs break-all">
                    {endpointName}
                  </code>
                </div>
                {endpointPath && (
                  <div className="flex items-start justify-between py-1">
                    <span className="text-chariot-text-secondary text-xs font-medium">
                      Path:
                    </span>
                    <span className="text-chariot-text-primary font-mono text-xs">
                      {endpointPath}
                    </span>
                  </div>
                )}
                {httpStatus && (
                  <div className="flex items-start justify-between py-1">
                    <span className="text-chariot-text-secondary text-xs font-medium">
                      Status:
                    </span>
                    <span className="text-chariot-text-primary text-xs">
                      {httpStatus}
                    </span>
                  </div>
                )}
                {exposureLevel && (
                  <div className="flex items-start justify-between py-1">
                    <span className="text-chariot-text-secondary text-xs font-medium">
                      Exposure:
                    </span>
                    <span className="text-warning text-xs font-medium">
                      {exposureLevel}
                    </span>
                  </div>
                )}
                {cloudPlatform && (
                  <div className="flex items-start justify-between py-1">
                    <span className="text-chariot-text-secondary text-xs font-medium">
                      Platform:
                    </span>
                    <span className="text-chariot-text-primary text-xs">
                      {cloudPlatform}
                    </span>
                  </div>
                )}
                {protocols && (
                  <div className="flex items-start justify-between py-1">
                    <span className="text-chariot-text-secondary text-xs font-medium">
                      Protocol:
                    </span>
                    <span className="text-chariot-text-primary text-xs">
                      {protocols}
                    </span>
                  </div>
                )}
                {exposureDescription && (
                  <div className="border-chariot-stroke border-t pt-2">
                    <span className="text-chariot-text-secondary mb-1 block text-xs font-medium">
                      Exposure Details:
                    </span>
                    <p className="text-chariot-text-tertiary text-xs">
                      {exposureDescription}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Library Vulnerability */}
            <div className="border-chariot-stroke rounded-lg border">
              <div className="border-chariot-stroke border-b px-4 py-3">
                <h4 className="text-chariot-text-primary flex items-center font-semibold">
                  <ExclamationTriangleIcon className="text-error mr-2 size-4" />
                  Library Vulnerability
                </h4>
              </div>
              <div className="space-y-3 p-4">
                {cveId && (
                  <div>
                    <span className="text-chariot-text-secondary mb-1 block text-xs font-medium">
                      CVE ID:
                    </span>
                    <span className="bg-error-selected text-error inline-flex items-center rounded px-2 py-1 text-xs font-medium">
                      {cveId}
                    </span>
                  </div>
                )}
                {detectedLibrary && (
                  <div className="flex items-start justify-between py-1">
                    <span className="text-chariot-text-secondary text-xs font-medium">
                      Library:
                    </span>
                    <span className="text-chariot-text-primary font-mono text-xs">
                      {detectedLibrary}
                    </span>
                  </div>
                )}
                {currentVersion && (
                  <div>
                    <span className="text-chariot-text-secondary mb-1 block text-xs font-medium">
                      Current Version:
                    </span>
                    <code className="bg-error-selected text-error rounded px-2 py-1 text-xs">
                      {currentVersion}
                    </code>
                  </div>
                )}
                {fixedVersion && (
                  <div>
                    <span className="text-chariot-text-secondary mb-1 block text-xs font-medium">
                      Fixed Version:
                    </span>
                    <code className="bg-success-selected text-success rounded px-2 py-1 text-xs">
                      {fixedVersion}
                    </code>
                  </div>
                )}
                {severity && (
                  <div className="flex items-start justify-between py-1">
                    <span className="text-chariot-text-secondary text-xs font-medium">
                      Severity:
                    </span>
                    <span className="text-error text-xs font-bold">
                      {severity}
                    </span>
                  </div>
                )}
                {language && (
                  <div className="flex items-start justify-between py-1">
                    <span className="text-chariot-text-secondary text-xs font-medium">
                      Language:
                    </span>
                    <span className="text-chariot-text-primary text-xs">
                      {language}
                    </span>
                  </div>
                )}
                {filePath && (
                  <div>
                    <span className="text-chariot-text-secondary mb-1 block text-xs font-medium">
                      File Path:
                    </span>
                    <code className="bg-chariot-background-secondary block rounded px-2 py-1 text-xs break-all">
                      {filePath}
                    </code>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Vulnerability Description */}
          {vulnDescription && (
            <div className="border-chariot-stroke rounded-lg border p-4">
              <h5 className="text-chariot-text-primary mb-3 font-medium">
                Vulnerability Details
              </h5>
              <p className="text-chariot-text-secondary text-sm leading-relaxed">
                {vulnDescription}
              </p>
              {publishedDate && (
                <div className="border-chariot-stroke mt-3 border-t pt-3">
                  <span className="text-chariot-text-secondary text-xs font-medium">
                    Published:
                  </span>
                  <span className="text-chariot-text-primary ml-2 text-xs">
                    {new Date(publishedDate).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Remediation */}
          {remediation && (
            <div className="border-success border-l-4 p-4">
              <div className="flex items-start">
                <CheckCircleIcon className="text-success mt-0.5 mr-3 size-5 shrink-0" />
                <div>
                  <h5 className="text-chariot-text-primary mb-2 font-semibold">
                    Remediation
                  </h5>
                  <code className="bg-chariot-background-secondary block rounded px-3 py-2 text-sm">
                    {remediation}
                  </code>
                  {wizId && (
                    <div className="mt-3">
                      <span className="text-chariot-text-secondary text-xs font-medium">
                        Wiz ID:
                      </span>
                      <code className="bg-chariot-background-secondary ml-2 rounded px-2 py-1 text-xs">
                        {wizId}
                      </code>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      );
      tempTabs['DETECTION'].push({
        label: 'API Vulnerability',
        value: 'wiz_endpoint_vuln',
      });
    }

    return processedKeys; // Return the keys we processed successfully
  } catch (error) {
    console.error('Error processing Wiz evidence:', error);
    const errorMessage = error instanceof Error ? error.message : String(error);

    sections['wiz_error'] = (
      <div className="border-error-light bg-error-selected rounded border p-4">
        <h4 className="text-error mb-2 font-semibold">
          Wiz Evidence Processing Error
        </h4>
        <p className="text-error text-sm">
          Unable to process Wiz evidence data: {errorMessage}
        </p>
        <details className="mt-3">
          <summary className="text-error cursor-pointer text-xs">
            Show raw data
          </summary>
          <pre className="bg-chariot-background-primary mt-2 max-h-32 overflow-auto rounded border p-2 text-xs">
            {JSON.stringify(poeData, null, 2)}
          </pre>
        </details>
      </div>
    );
    if (mainTabKeys.includes('INFORMATION')) {
      tempTabs['INFORMATION'].push({
        label: 'Processing Error',
        value: 'wiz_error',
      });
    }
    return []; // Return empty array on error
  }
};

const processHadrianDataSource = (
  poeData: POEContent,
  sections: Record<string, SectionContent>,
  tempTabs: Record<MainTabKey, TabOption[]>,
  copyContents?: Record<string, string>
) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const data = poeData as Record<string, any>;
  const evidence = data?.evidence || {};
  const req = evidence?.request || {};
  const res = evidence?.response || {};

  const requestHeaders = req?.headers || {};
  const responseHeaders = res?.headers || {};
  const requestBody = req?.body || '';
  let responseBody = res?.body || '';
  if (typeof responseBody === 'string') {
    try {
      responseBody = JSON.stringify(JSON.parse(responseBody), null, 2);
    } catch {
      // keep raw string when not JSON
    }
  }

  sections['hadrian_summary'] = (
    <div className="border-chariot-stroke overflow-hidden rounded-lg border">
      <table className="w-full">
        <tbody>
          <tr className="border-chariot-stroke border-b last:border-b-0">
            <td className="bg-chariot-background-secondary text-chariot-text-secondary w-1/3 px-4 py-3 font-medium">
              Category
            </td>
            <td className="text-chariot-text-primary px-4 py-3">
              {data?.category || '-'}
            </td>
          </tr>
          <tr className="border-chariot-stroke border-b last:border-b-0">
            <td className="bg-chariot-background-secondary text-chariot-text-secondary px-4 py-3 font-medium">
              Finding
            </td>
            <td className="text-chariot-text-primary px-4 py-3">
              {data?.name || '-'}
            </td>
          </tr>
          <tr className="border-chariot-stroke border-b last:border-b-0">
            <td className="bg-chariot-background-secondary text-chariot-text-secondary px-4 py-3 font-medium">
              Endpoint
            </td>
            <td className="text-chariot-text-primary px-4 py-3 font-mono text-sm">
              {`${data?.method || ''} ${data?.endpoint || ''}`.trim() || '-'}
            </td>
          </tr>
          <tr className="border-chariot-stroke border-b last:border-b-0">
            <td className="bg-chariot-background-secondary text-chariot-text-secondary px-4 py-3 font-medium">
              Attacker Role
            </td>
            <td className="text-chariot-text-primary px-4 py-3">
              {data?.attacker_role || '-'}
            </td>
          </tr>
          <tr className="border-chariot-stroke border-b last:border-b-0">
            <td className="bg-chariot-background-secondary text-chariot-text-secondary px-4 py-3 font-medium">
              Severity
            </td>
            <td className="text-chariot-text-primary px-4 py-3">
              {data?.severity || '-'}
            </td>
          </tr>
          <tr className="border-chariot-stroke border-b last:border-b-0">
            <td className="bg-chariot-background-secondary text-chariot-text-secondary px-4 py-3 font-medium">
              Timestamp
            </td>
            <td className="text-chariot-text-primary px-4 py-3">
              {data?.timestamp ? formatDate(data.timestamp) : '-'}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
  tempTabs['INFORMATION'].push({ label: 'Summary', value: 'hadrian_summary' });

  if (Array.isArray(data?.request_ids) && data.request_ids.length > 0) {
    sections['hadrian_request_ids'] = (
      <div className="space-y-2">
        {data.request_ids.map((id: string) => (
          <div
            key={id}
            className="text-chariot-text-secondary flex items-center gap-2 rounded border px-2 py-1 font-mono text-xs"
          >
            <span>{id}</span>
          </div>
        ))}
      </div>
    );
    tempTabs['DETECTION'].push({
      label: 'Request IDs',
      value: 'hadrian_request_ids',
    });
  }

  sections['hadrian_detection'] =
    data?.description ||
    data?.id ||
    'No additional detection metadata provided.';
  tempTabs['DETECTION'].push({
    label: 'Detection',
    value: 'hadrian_detection',
  });

  const requestRaw = JSON.stringify(
    {
      method: req?.method || data?.method || '',
      url: req?.url || data?.endpoint || '',
      headers: requestHeaders,
      body: requestBody,
    },
    null,
    2
  );
  sections['hadrian_request'] = requestRaw;
  tempTabs['REQUEST'].push({ label: 'Request', value: 'hadrian_request' });
  if (copyContents) copyContents['hadrian_request'] = requestRaw;

  const responseRaw = JSON.stringify(
    {
      status_code: res?.status_code,
      headers: responseHeaders,
      body: responseBody,
      body_hash: res?.body_hash,
      size: res?.size,
      truncated: res?.truncated,
    },
    null,
    2
  );
  sections['hadrian_response'] = responseRaw;
  tempTabs['RESPONSE'].push({ label: 'Response', value: 'hadrian_response' });
  if (copyContents) copyContents['hadrian_response'] = responseRaw;

  // Keep raw POE for debugging
  const rawData = JSON.stringify(data, null, 2);
  sections['hadrian_raw_data'] = rawData;
  tempTabs['INFORMATION'].push({
    label: 'Raw Data',
    value: 'hadrian_raw_data',
  });
  if (copyContents) copyContents['hadrian_raw_data'] = rawData;
};

const processGenericDataSource = (
  poeData: POEContent,
  sections: Record<string, SectionContent>,
  tempTabs: Record<MainTabKey, TabOption[]>,
  theme: ThemeOption,
  mainTabKeys: MainTabKey[],
  copyContents?: Record<string, string>
) => {
  if (typeof poeData === 'object' && poeData !== null) {
    Object.entries(poeData).forEach(([key, value]) => {
      if (key === 'repository_url' && typeof value === 'string') {
        const repoInfo = getRepoInfo(value, theme);
        if (repoInfo) {
          sections[repoInfo.tab] = repoInfo.content;
          if (mainTabKeys.includes('ATTRIBUTES')) {
            tempTabs['ATTRIBUTES'].push({
              label: repoInfo.tab,
              value: repoInfo.tab.toLowerCase(),
            });
          }
        }
      } else {
        const formattedKey = formatKey(key);
        sections[key] =
          typeof value === 'string' ? value : JSON.stringify(value, null, 2);

        const mainTab = mapToMainTab(formattedKey, mainTabKeys);
        tempTabs[mainTab].push({
          label: formattedKey,
          value: key,
        });
      }
    });
  }
  // Always add Raw Data section for unknown sources, even if poeData is empty or a string
  if (
    mainTabKeys.includes('INFORMATION') &&
    poeData &&
    (typeof poeData === 'string' || Object.keys(poeData).length > 0)
  ) {
    const rawData =
      typeof poeData === 'string' ? poeData : JSON.stringify(poeData, null, 2);
    // Store plain text version for copying
    if (copyContents && rawData) {
      copyContents['data'] = rawData.trim();
    }
    sections['data'] = (
      <pre className="text-chariot-text-primary m-0 max-h-96 overflow-auto border-0 bg-transparent p-0 font-mono text-sm whitespace-pre-wrap">
        {rawData?.trim()}
      </pre>
    );
    tempTabs['INFORMATION'].push({ label: 'Data', value: 'data' });
  }
};

function addLoginPanelTab({
  sections,
  tempTabs,
  webpage,
  poeData,
}: {
  sections: Record<string, SectionContent>;
  tempTabs: Record<MainTabKey, TabOption[]>;
  webpage: Webpage;
  poeData?: POEContent;
}) {
  const screenshot = webpage.screenshot;

  const proofInputFields = Array.isArray(poeData?.input_fields)
    ? poeData.input_fields
    : [];
  const allInputFields = Array.from(new Set(proofInputFields.filter(Boolean)));

  const foundAtURL =
    poeData?.found_at_url || poeData?.['matched-at'] || webpage.url;
  const resolvedTo = poeData?.resolved_to;
  const matchedAtURL = poeData?.matched_at;
  const detectorName = poeData?.detector_name;
  const snippet = poeData?.snippet;
  const crawledAt = poeData?.crawled_at || webpage.created;
  const sourceURL = poeData?.source_url || webpage.url;
  const method = poeData?.method || 'GET';

  const contentLength =
    typeof poeData?.content_length === 'number'
      ? poeData.content_length.toString()
      : webpage.metadata?.content_length?.toString() ||
        (snippet ? new Blob([snippet]).size.toString() : '');

  tempTabs['LOGIN_PANEL'].push({
    label: 'Request',
    value: 'login_panel_request',
  });
  sections['login_panel_request'] = (
    <div className="text-chariot-text-secondary grid grid-cols-2 gap-2 text-sm">
      <div className="col-span-2 flex items-center gap-2">
        <span>Request URL:</span>
        <Link
          to={foundAtURL}
          target="_blank"
          rel="noopener noreferrer"
          buttonClass="text-left p-0"
        >
          <span>{foundAtURL}</span>
        </Link>
      </div>
      {resolvedTo && resolvedTo !== foundAtURL && (
        <div className="col-span-2 flex items-center gap-2">
          <span>Resolved To:</span>
          <Link
            to={resolvedTo}
            target="_blank"
            rel="noopener noreferrer"
            buttonClass="text-left p-0"
          >
            <span>{resolvedTo}</span>
          </Link>
        </div>
      )}
      <LoginPanelRequestCell
        label="Crawled"
        value={formatDate(crawledAt)}
        copy={false}
      />
      <LoginPanelRequestCell label="Method" value={method} />
      <LoginPanelRequestCell label="Source" value={sourceURL} />
      {contentLength && (
        <LoginPanelRequestCell label="Content-Length" value={contentLength} />
      )}
    </div>
  );

  if (detectorName || matchedAtURL) {
    tempTabs['LOGIN_PANEL'].push({
      label: 'Detection',
      value: 'login_panel_detection',
    });
    sections['login_panel_detection'] = (
      <div className="text-chariot-text-secondary grid grid-cols-2 gap-2 text-sm">
        {detectorName && (
          <LoginPanelRequestCell label="Detector" value={detectorName} />
        )}
        {matchedAtURL && (
          <div className="col-span-2 flex flex-col gap-1">
            <span className="text-chariot-text-tertiary text-xs">
              Matched At:
            </span>
            <Link
              to={matchedAtURL}
              target="_blank"
              rel="noopener noreferrer"
              buttonClass="text-left p-0"
              className="text-xs break-all"
            >
              <span className="text-xs break-all">{matchedAtURL}</span>
            </Link>
          </div>
        )}
      </div>
    );
  }

  if (snippet) {
    tempTabs['LOGIN_PANEL'].push({
      label: 'Matched Evidence',
      value: 'login_panel_snippet',
    });
    sections['login_panel_snippet'] = (
      <pre className="text-chariot-text-primary m-0 max-h-96 overflow-auto border-0 bg-transparent p-0 font-mono text-xs whitespace-pre-wrap">
        {snippet}
      </pre>
    );
  }

  if (allInputFields.length > 0) {
    tempTabs['LOGIN_PANEL'].push({
      label: 'Discovered Input Fields',
      value: 'login_panel_inputs',
    });
    sections['login_panel_inputs'] = (
      <div className="flex flex-wrap items-center gap-2">
        {allInputFields.map(field => (
          <DashedTag
            copy={true}
            key={field}
            className="px-3 py-0.5 text-xs normal-case"
            label={field}
          />
        ))}
      </div>
    );
  }
  if (screenshot) {
    tempTabs['LOGIN_PANEL'].push({
      label: 'Screenshots',
      value: 'login_panel_screenshots',
    });
    sections['login_panel_screenshots'] = (
      <MarkdownPreview
        source={`![${screenshot?.split('/').pop()}](#file/${screenshot})`}
        thumbnail={true}
      />
    );
  }
}

// Dedicated renderer for GitHub (gato) evidence
function renderGatoEvidence(poeData: GitHubPOE) {
  const { scopes, enumeration } = poeData;
  const { timestamp, repositories } = enumeration;
  return (
    <div className="space-y-8">
      {repositories.map(repo => {
        const repoName = repo.name;
        const permissions = repo.permissions;
        const canFork = repo.can_fork;
        const runnerWorkflows = repo.runner_workflows || [];
        const repoSecrets = repo.repo_secrets || [];
        const orgSecrets = repo.org_secrets || [];
        return (
          <div key={repoName} className="space-y-4">
            {/* Header Block */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <span className="text-chariot-text-secondary text-sm">
                  Repository:
                </span>
                <CopyToClipboard alwaysShowCopyIcon textToCopy={repoName}>
                  <span className="text-chariot-text-primary text-sm font-semibold">
                    {repoName}
                  </span>
                </CopyToClipboard>
              </div>
              {scopes && scopes.length > 0 && (
                <div className="text-sm">
                  <span className="text-chariot-text-secondary font-semibold">
                    Scopes:
                  </span>
                  <div className="mt-1 flex flex-wrap gap-4">
                    {scopes.map(scope => (
                      <CopyToClipboard
                        key={scope}
                        alwaysShowCopyIcon
                        textToCopy={scope}
                      >
                        <DashedTag
                          label={scope}
                          size="sm"
                          className="text-chariot-text-primary"
                        />
                      </CopyToClipboard>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {/* Runner Workflows */}
            {runnerWorkflows.length > 0 && (
              <div className="text-sm">
                <div className="text-chariot-text-secondary font-semibold">
                  Workflows:
                </div>
                <div className="mt-1 flex flex-wrap gap-4">
                  {runnerWorkflows.map(wf => (
                    <CopyToClipboard
                      key={wf}
                      alwaysShowCopyIcon
                      textToCopy={wf}
                    >
                      <BaseTag size="sm">{wf}</BaseTag>
                    </CopyToClipboard>
                  ))}
                </div>
              </div>
            )}
            {/* Permissions Table */}
            <div>
              <div className="text-chariot-text-secondary mb-1 font-semibold">
                Permissions:
              </div>
              <table className="border-chariot-stroke w-full rounded border text-sm">
                <thead>
                  <tr className="bg-chariot-background-secondary">
                    <th className="text-chariot-text-secondary px-3 py-2 text-left font-semibold">
                      Permission
                    </th>
                    <th className="text-chariot-text-secondary px-3 py-2 text-left font-semibold">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {['admin', 'maintain', 'push', 'triage', 'pull'].map(perm => (
                    <tr key={perm}>
                      <td className="text-chariot-text-primary px-3 py-2 capitalize">
                        {perm}
                      </td>
                      <td className="px-3 py-2">
                        {permissions[perm as keyof typeof permissions] ? (
                          <Check className="text-chariot-stroke-info inline size-4 align-middle" />
                        ) : (
                          <X className="text-chariot-stroke-critical inline size-4 align-middle" />
                        )}
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td className="text-chariot-text-primary px-3 py-2">
                      Can Fork
                    </td>
                    <td className="px-3 py-2">
                      {canFork ? (
                        <Check className="text-chariot-stroke-info inline size-4 align-middle" />
                      ) : (
                        <X className="text-chariot-stroke-critical inline size-4 align-middle" />
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            {/* Secrets/Org Secrets */}
            {(repoSecrets.length > 0 || orgSecrets.length > 0) && (
              <div className="flex flex-wrap gap-2">
                {repoSecrets.length > 0 && (
                  <span className="bg-chariot-status-severe/10 text-chariot-status-severe inline-block rounded px-2 py-0.5 text-xs font-semibold">
                    Repo Secrets Present
                  </span>
                )}
                {orgSecrets.length > 0 && (
                  <span className="bg-chariot-status-severe/10 text-chariot-status-severe inline-block rounded px-2 py-0.5 text-xs font-semibold">
                    Org Secrets Present
                  </span>
                )}
              </div>
            )}
            {/* Enumeration Time */}
            <div className="text-chariot-text-tertiary mt-2 text-xs">
              Last Scanned: {formatDate(timestamp)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export const POE: React.FC<POEProps> = ({
  poeLocation,
  fallbackPOELocation,
  severity,
  name,
  source,
  attributes = [],
  className,
  // definitionsFileValue,
  riskKey,
  webpage,
}) => {
  const { keyboardListeners } = useGlobalState();
  const { currentTheme } = useTheme();

  const { data: evidenceFile } = useGetFile(
    {
      name: `evidence/${riskKey}`,
    },
    {
      enabled: !!name,
    }
  );

  const evidenceFileValue =
    typeof evidenceFile === 'string'
      ? evidenceFile
      : evidenceFile
        ? JSON.stringify(evidenceFile)
        : '';

  // Initialize selectedTab state
  const [selectedTabState, setSelectedTab] = useState<ExtendedTabOption | null>(
    null
  );

  // Get file data and process sections
  const { data: poeFile, status: poeFileStatus } = useGetFile(
    {
      name: `proofs/${poeLocation}`,
    },
    {
      enabled: !!poeLocation,
    }
  );

  const enableFallback =
    !!fallbackPOELocation &&
    poeFileStatus !== 'pending' &&
    (poeFileStatus === 'error' || (poeFileStatus === 'success' && !poeFile));

  const { data: fallbackFile, status: fallbackStatus } = useGetFile(
    {
      name: `proofs/${fallbackPOELocation}`,
    },
    {
      enabled: enableFallback,
    }
  );

  const file = enableFallback ? fallbackFile : poeFile;
  const fileStatus = enableFallback ? fallbackStatus : poeFileStatus;

  const sectionsRef = useRef<HTMLDivElement>(null);

  const mainTabKeys = useMemo(() => getMainTabsForSource(source), [source]);

  const { sections, availableTabs, copyContents } = useMemo(() => {
    // Loading guard: if main or fallback file is still loading, return empty
    if (
      fileStatus === 'pending' ||
      (enableFallback && fallbackStatus === 'pending')
    ) {
      return { sections: {}, availableTabs: [], copyContents: {} };
    }

    /**
     * Helper function to create an image section from a blob URL
     * Reduces code duplication for evidence and file image rendering
     *
     * @param imageSource - The image source (should be a blob URL string)
     * @param registryKey - Unique key for blob URL registry
     * @param label - Label for the tab
     * @returns Object with section and tabOption, or null if not a valid blob URL
     */
    function createImageSection(
      imageSource: unknown,
      registryKey: string,
      label: string = 'Uploaded Evidence'
    ): { section: React.ReactNode; tabOption: TabOption } | null {
      if (typeof imageSource !== 'string' || !imageSource.startsWith('blob:')) {
        return null;
      }

      return {
        section: (
          <EvidenceImage
            src={imageSource}
            altText="Evidence"
            registryKey={registryKey}
          />
        ),
        tabOption: { label, value: registryKey },
      };
    }

    function getPOESections() {
      try {
        const poeData = file as POEContent;
        const sections: Record<string, SectionContent> = {};
        const copyContents: Record<string, string> = {}; // Store plain text for copying
        const tempTabs: Record<MainTabKey, TabOption[]> = {
          REQUEST: [],
          RESPONSE: [],
          DETECTION: [],
          ATTRIBUTES: [],
          LOGIN_PANEL: [],
          INFORMATION: [],
          REMEDIATION: [],
        };

        // Structured-proof sentinel: when the body advertises format='elements/v1',
        // emit one sub-tab per section so POE's existing sub-tab card wrapper
        // gives each section a discrete bordered card. The renderer renders
        // just the elements of the section (no extra section chrome).
        if (isStructuredProof(file)) {
          file.sections.forEach((section, idx) => {
            const value = `structured_proof_${idx}`;
            sections[value] = (
              <ProofRenderer
                proof={{ format: file.format, sections: [section] }}
                bare
              />
            );
            tempTabs['INFORMATION'].push({
              label: section.title || 'Evidence',
              value,
            });
          });
          return {
            sections,
            availableTabs: Object.keys(tempTabs)
              .filter(key => tempTabs[key as MainTabKey].length > 0)
              .map(key => {
                const tabDef = MAIN_TAB_DEFS[key as MainTabKey];
                return {
                  label: (
                    <div className="flex items-center gap-3">
                      <tabDef.icon className="size-4" />
                      <span>{tabDef.label}</span>
                    </div>
                  ),
                  heading: tabDef.heading || tabDef.label,
                  value: tabDef.id,
                  id: tabDef.id,
                  subTabs: tempTabs[key as MainTabKey],
                };
              }) as ExtendedTabOption[],
            copyContents,
          };
        }

        // Handle evidence image files (when evidenceFile is a blob URL from useGetFile)
        const evidenceImage = createImageSection(
          evidenceFileValue,
          'evidence_image',
          'Uploaded Evidence'
        );

        if (evidenceImage) {
          sections['evidence_image'] = evidenceImage.section;
          if (mainTabKeys.includes('INFORMATION')) {
            tempTabs['INFORMATION'].push(evidenceImage.tabOption);
          }
        }

        // Handle image files (when file is a blob URL from useGetFile)
        const mainImage = createImageSection(
          file,
          'uploaded_image',
          'Uploaded Evidence'
        );
        if (mainImage) {
          sections['uploaded_image'] = mainImage.section;
          if (mainTabKeys.includes('INFORMATION')) {
            tempTabs['INFORMATION'].push(mainImage.tabOption);
          }

          // Early return for image-only files
          return {
            sections,
            availableTabs: Object.keys(tempTabs)
              .filter(key => tempTabs[key as MainTabKey].length > 0)
              .map(key => {
                const tabDef = MAIN_TAB_DEFS[key as MainTabKey];
                return {
                  label: (
                    <div className="flex items-center gap-3">
                      <tabDef.icon className="size-4" />
                      <span>{tabDef.label}</span>
                    </div>
                  ),
                  heading: tabDef.heading || tabDef.label,
                  value: tabDef.id,
                  id: tabDef.id,
                  subTabs: tempTabs[key as MainTabKey],
                };
              }) as ExtendedTabOption[],
          };
        }

        // Custom renderer for GitHub (gato) evidence
        if (
          source &&
          source.startsWith('gato') &&
          poeData.username &&
          poeData.enumeration &&
          Array.isArray(poeData.enumeration.repositories)
        ) {
          sections['github_evidence'] = renderGatoEvidence(
            poeData as GitHubPOE
          );
          if (tempTabs['INFORMATION']) {
            tempTabs['INFORMATION'].push({
              label: 'GitHub Evidence',
              value: 'github_evidence',
            });
          }
          return {
            sections,
            availableTabs: Object.keys(tempTabs)
              .filter(key => tempTabs[key as MainTabKey].length > 0)
              .map(key => {
                const tabDef = MAIN_TAB_DEFS[key as MainTabKey];
                return {
                  label: (
                    <div className="flex items-center gap-3">
                      <tabDef.icon className="size-4" />
                      <span>{tabDef.label}</span>
                    </div>
                  ),
                  heading: tabDef.heading || tabDef.label,
                  value: tabDef.id,
                  id: tabDef.id,
                  subTabs: tempTabs[key as MainTabKey],
                };
              }) as ExtendedTabOption[],
          };
        }

        const summary = poeData?.info
          ? typeof poeData.info === 'string'
            ? (decode(poeData.info, {}) as SummaryPOE)
            : (poeData.info as unknown as SummaryPOE)
          : undefined;

        const vulnerabilityClassificationData = getVulnerabilityClassification(
          summary,
          severity
        );
        if (vulnerabilityClassificationData.some(({ value }) => !!value)) {
          sections['vuln_classification'] = (
            <VulnerabilityClassification
              data={vulnerabilityClassificationData}
              severity={severity}
            />
          );
          if (mainTabKeys.includes('INFORMATION')) {
            tempTabs['INFORMATION'].push({
              label: 'Vulnerability Classification',
              value: 'vuln_classification',
            });
          }
        }

        // Only create attributes section if we have data to display
        if (attributes && attributes.length > 0) {
          const attributesTableData = attributes
            .sort((a, b) => a.name.localeCompare(b.name))
            .map(attribute => ({
              name: attribute.name,
              value: attribute.value,
              lastSeen: formatDate(attribute.visited),
            }));

          sections['attributes'] = (
            <Table
              name="attributes"
              data={attributesTableData}
              columns={[
                {
                  label: 'Name',
                  id: 'name',
                },
                {
                  label: 'Value',
                  id: 'value',
                  cell: row =>
                    row.value ? (
                      <div className="text-chariot-text-secondary flex w-fit items-center rounded px-2 py-1">
                        <span> {row.value}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="Copy to clipboard"
                          onClick={() => handleCopy(row.value)}
                        >
                          <CopyIcon className="size-4" />
                        </Button>
                      </div>
                    ) : (
                      '-'
                    ),
                },
                {
                  label: 'Last Seen',
                  id: 'lastSeen',
                  align: 'right',
                  fixedWidth: 100,
                },
              ]}
              status="success"
              error={null}
              selection={{ hide: true }}
            />
          );

          if (mainTabKeys.includes('ATTRIBUTES')) {
            tempTabs['ATTRIBUTES'].push({
              label: 'Attributes',
              value: 'attributes',
            });
          }
        }

        if (source.startsWith('burp')) {
          processBurpDataSource(
            poeData,
            sections,
            tempTabs,
            mainTabKeys,
            copyContents
          );
        } else if (source.startsWith('login-detection')) {
          // Login-detection proof is rendered entirely by the Login Panel below.
          // Skip nuclei renderer to avoid duplicate/confusing attribute display.
        } else if (source.startsWith('nuclei') || source === 'cve-2025-55182') {
          processNucleiDataSource(poeData, sections, tempTabs, mainTabKeys);
        } else if (
          source.startsWith('webpage-secrets') ||
          source.startsWith('crawler') ||
          source.startsWith('confluence-secrets')
        ) {
          processCrawlerDataSource(file, sections, tempTabs, true, mainTabKeys);
        } else if (
          source.startsWith('secrets') ||
          source.includes('find-secrets') ||
          source.includes('-secrets')
        ) {
          // Handle all secret scanners: repo-based (secrets) and cloud-based (nebula-*-find-secrets)
          processSecretsDataSource(file, sections, tempTabs);
        } else if (source.startsWith('augustus')) {
          // Handle Augustus LLM vulnerability scanner
          processAugustusDataSource(file, sections, tempTabs);
        } else if (source.startsWith('constantine')) {
          // Handle Constantine LLM source code vulnerability scanner
          processConstantineDataSource(file, sections, tempTabs);
        } else if (
          source.startsWith('portscan') ||
          source.startsWith('fingerprint') ||
          name.startsWith('exposure-')
        ) {
          processPortscanDataSource(poeData, sections, tempTabs, mainTabKeys);
        } else if (source.startsWith('ssh')) {
          processSshDataSource(poeData, sections, tempTabs, mainTabKeys);
        } else if (source.startsWith('whois')) {
          processWhoisDataSource(poeData, sections, tempTabs, mainTabKeys);
        } else if (source.startsWith('orca')) {
          processOrcaDataSource(poeData, sections, tempTabs, mainTabKeys);
        } else if (source.startsWith('snyk')) {
          processSnykCodeDataSource(file, sections, tempTabs, mainTabKeys);
        } else if (source.startsWith('wiz')) {
          processWizDataSource(poeData, sections, tempTabs, mainTabKeys);
        } else if (source.startsWith('hadrian')) {
          processHadrianDataSource(poeData, sections, tempTabs, copyContents);
        } else {
          // step 1: handle generic data source
          processGenericDataSource(
            poeData,
            sections,
            tempTabs,
            currentTheme,
            mainTabKeys,
            copyContents
          );
        }

        if (webpage && name.startsWith('web-login')) {
          addLoginPanelTab({
            sections,
            tempTabs,
            webpage,
            poeData,
          });
        }

        // Return organized sections and main tabs
        return {
          sections,
          availableTabs: mainTabKeys
            .filter(
              key =>
                tempTabs[key].length > 0 ||
                key === 'ATTRIBUTES' ||
                (key === 'INFORMATION' && evidenceFileValue)
            )
            .map(key => {
              const tabDef = MAIN_TAB_DEFS[key];
              return {
                label: (
                  <div className="flex items-center gap-3">
                    <tabDef.icon className="size-4" />
                    <span>{tabDef.label}</span>
                  </div>
                ),
                heading: tabDef.heading || tabDef.label,
                value: tabDef.id,
                id: tabDef.id,
                subTabs: tempTabs[key],
              };
            }) as ExtendedTabOption[],
          copyContents,
        };
      } catch (error) {
        console.error('Error processing POE data:', error);
        return {
          sections: {},
          availableTabs: [] as ExtendedTabOption[],
          copyContents: {},
        };
      }
    }
    return getPOESections();
  }, [
    // Key identifiers that should force recalculation
    riskKey,
    poeLocation,
    name,
    source,
    // File data
    poeFile,
    fallbackFile,
    fileStatus,
    fallbackStatus,
    // Other dependencies
    severity,
    mainTabKeys,
    evidenceFileValue,
  ]);
  const selectedTab = selectedTabState || availableTabs[0];

  const scrollToSection = (label: string) => {
    if (sectionsRef.current) {
      const sectionElement = sectionsRef.current.querySelector(
        `[data-section="${label}"]`
      );
      if (sectionElement) {
        sectionElement.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  useEffect(() => {
    if (selectedTab && name) {
      keyboardListeners.add(
        KeyboardListersId.POE,
        {
          ArrowLeft: (event: KeyboardEvent) => {
            event.preventDefault();
            keyboardListeners.remove(KeyboardListersId.POE);
            keyboardListeners.setPOEEnabled(false);
          },
        },
        true
      );
      scrollToSection(selectedTab?.label as string);

      return () => {
        keyboardListeners.remove(KeyboardListersId.POE);
      };
    }
  }, [
    selectedTab,
    name,
    JSON.stringify({
      availableTabs: availableTabs.map(tab => tab.value),
    }),
  ]);

  useEffect(() => {
    return () => {
      keyboardListeners.setPOEEnabled(true);
    };
  }, []);

  // Track the last selected tab type across risks

  // Handle tab changes and remember the tab type
  const handleTabChange = (tab: ExtendedTabOption) => {
    setSelectedTab(tab);
    scrollToSection(tab.label as string);
    keyboardListeners.setPOEEnabled(true);
  };

  // Set tab when availableTabs are ready
  useEffect(() => {
    if (availableTabs.length > 0 && Object.keys(sections).length > 0) {
      // If the current selectedTab is not in availableTabs, reset it
      const validTab = availableTabs.find(
        tab => tab.value === selectedTab?.value
      );
      if (!validTab) {
        // Use queueMicrotask to avoid synchronous setState in effect
        queueMicrotask(() => {
          setSelectedTab(availableTabs[0]);
        });
        keyboardListeners.setPOEEnabled(true);
      }
    }
  }, [availableTabs, sections, riskKey]);

  // Handle copy with feedback
  const handleCopy = (content: string) => {
    navigator.clipboard.writeText(content).catch(console.error);
  };

  if (
    fileStatus === 'pending' ||
    (enableFallback && fallbackStatus === 'pending')
  ) {
    return <Loader isLoading className="m-4 w-[calc(100%-32px)]" />;
  }

  if (!file && availableTabs.length === 0) {
    return (
      <div className="my-4 flex flex-col items-center justify-center">
        <NoInformationIcon className="text-chariot-stroke mb-4" />
        <p className="text-chariot-text-primary text-lg font-bold">
          No Proof of Exploit Available
        </p>
      </div>
    );
  }

  return (
    <>
      <div
        className={cn('flex h-full overflow-hidden', className)}
        data-testid="evidence-tab"
      >
        {/* Main layout container */}
        <div className="flex w-full gap-6">
          {/* Left sidebar with tabs */}
          <div className="border-chariot-stroke w-48 shrink-0 overflow-y-auto border-r py-4">
            <Tabs
              vertical={true}
              variant="secondary"
              id={KeyboardListersId.POE}
              keyboardNavigation={keyboardListeners.poeEnabled}
              tabs={availableTabs}
              selected={selectedTab || availableTabs[0]}
              align="left"
              onSelect={handleTabChange}
              className="[&>button]:justify-start [&>button]:capitalize"
            />
          </div>

          {/* Main content area */}
          <div className="flex-1 overflow-y-auto py-4 pr-4">
            {selectedTab && (
              <div className="flex flex-col gap-6">
                {/* Section header */}
                <div className="border-chariot-stroke pb-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-chariot-text-primary text-xl font-semibold">
                      {selectedTab.heading}
                    </h2>
                  </div>
                  <p className="text-chariot-text-secondary mt-1 text-sm">
                    <TabDescription
                      tabId={selectedTab.value}
                      definitionsFileValue={
                        evidenceFileValue?.startsWith('blob:')
                          ? ''
                          : evidenceFileValue
                      }
                    />
                  </p>
                </div>

                {/* Content */}
                <div className="flex flex-col gap-4">
                  {selectedTab.subTabs?.map((subTab: TabOption) => {
                    const copyContent = getSectionContent(
                      sections,
                      subTab.value,
                      copyContents
                    );
                    const displayContent = getDisplayContent(
                      sections,
                      subTab.value
                    );
                    return displayContent ? (
                      <div
                        key={subTab.value}
                        className="border-chariot-stroke bg-chariot-background-primary overflow-hidden rounded-md border"
                      >
                        <div className="border-chariot-stroke flex items-center justify-between border-b px-4 py-3">
                          <h3 className="text-chariot-text-primary flex items-center gap-2 font-semibold">
                            {subTab.value === 'matched_at' ? (
                              <CheckCircleIcon className="text-chariot-stroke-info size-6" />
                            ) : null}
                            <span>{subTab.label}</span>
                          </h3>
                          {copyContent && (
                            <CopyToClipboard
                              textToCopy={copyContent}
                              alwaysShowCopyIcon
                              iconClassName="size-4 text-chariot-text-secondary"
                            />
                          )}
                        </div>
                        <div className="p-4">
                          <Content content={displayContent} />
                        </div>
                      </div>
                    ) : null;
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

// Re-exports for external consumers (POESelector, TabDescription, TechnologyEvidence)
// eslint-disable-next-line react-refresh/only-export-components
export { MAIN_TAB_DEFS } from '@/sections/poe/constants/mainTabDefs';
export type { POEProps } from '@/sections/poe/types/poe.types';
